/**
 * Deep Research Global Tool
 *
 * Multi-stage research workflow using Perplexity:
 * - Stage 1: Qualification - Analyze query, determine if deep research needed
 * - Stage 2: Strategy - Build research brief (internal)
 * - Stage 3: Execution - Run deep research with streaming
 *
 * Supports document attachments for context enrichment.
 */

import { buildModelDisplay } from '../modelDisplay';
import { withOncallNotice } from '../../../config/oncallNotice';
import { runPerplexityResearch, runPerplexityResearchStream, PERPLEXITY_PROXY_URL_DIRECT } from '../../perplexityClient';
import { analyzeDocumentsWithClaude, hasAnalyzableFiles } from '../../claudeDocumentAnalyzer';
import {
  finalizeAttachmentReadComplete,
  createStatusBadge,
  createProgressBar,
} from '../messaging';
import { getPendingWidgetHtml } from '../widgetHtml';
import { generateDualFormatDocumentsWithStorage } from '../../../utils/documentGenerator';
import { fetchCurrentTenantBranding } from '../../tenantBranding';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import {
  PERPLEXITY_DEEP_RESEARCH_MODEL,
  PERPLEXITY_DEEP_RESEARCH_DISPLAY,
  PERPLEXITY_DEEP_RESEARCH_MAX_TOKENS,
  PERPLEXITY_DEEP_RESEARCH_STAGE1_MODEL,
  PERPLEXITY_DEEP_RESEARCH_STAGE1_DISPLAY,
  PERPLEXITY_DEEP_RESEARCH_STAGE2_MODEL,
  PERPLEXITY_DEEP_RESEARCH_STAGE2_DISPLAY,
  PERPLEXITY_DEEP_RESEARCH_STAGES_1_2_REASONING_EFFORT,
  PERPLEXITY_REASONING_EFFORT,
  PERPLEXITY_SEARCH_CONTEXT_SIZE,
  PERPLEXITY_DEEP_RESEARCH_FALLBACK_MODEL,
  CLAUDE_DOCUMENT_ANALYSIS_DISPLAY,
} from '../../../config/agenticWorkflowConfig';
import {
  STAGE1_SYSTEM_PROMPT,
  STAGE2_SYSTEM_PROMPT,
  buildDeepResearchSystemPrompt,
  CHAT_VIDEO_SCRIPT_BUTTON_ID,
} from '../../agenticWorkflows/constants';
import { BUTTON_IDS as TRANSLATE_BUTTON_IDS } from './translate';
import { isAgenticWorkflowsAuthorized } from '../../agenticWorkflows';
import {
  parseDeepResearchPrompt,
  parseStage1Json,
  parseInitialContextSections,
  formatDeepResearchQualificationHtml,
  formatResearchToHtml,
  formatPerplexityError,
  stripThinkingBlocks,
  generateConciseTitle,
  generateSpecialMessageTemplate,
  generateResearchActionButtonsHtml,
  isCancelCommand,
  generateQueryBasedInitialContext,
  ensureReferencesSection,
  findReferencesSectionIndex,
  appendMissingCitationsToReferences,
  buildDeepResearchQuickAnswerCtaHtml,
  PREST0_PRIMARY_CTA_BG,
  PREST0_SECONDARY_CTA_BG,
} from '../../agenticWorkflows/helpers';
import { hasNegationAround } from '../triggerHelpers';
import { detectNoClarifyInstruction } from '../triggerHelpers';
import { detectInputLanguage, buildLanguageDirective } from '../../agenticWorkflows/languageMirror';
import { broadcastBridgeEvent } from '../../agenticWorkflows/broadcastBridgeEvent';
import { isSkipClarificationSentinel } from '../hooks/awaitingInputBreakOut';
// 2026-06 reliability overhaul — raw-dump filter shared with the resume
// notices: server error strings must never render JSON/stack payloads.
import { sanitizeServerErrorNotice } from '../../../components/GlobalChatSystem/serverTaskResume';

// Import button ID for video script action
import { BUTTON_IDS as VIDEO_SCRIPT_BUTTON_IDS } from './videoScript';

// Import workflow output store for button context passing
import { storeWorkflowOutput } from '../workflowOutputStore';

// Point 6 (Option B) — server-side durable GUEST deep-research client.
import {
  startGuestDeepResearch,
  pollGuestTaskUntilDone,
  mintGuestClientTaskId,
  GuestResearchRateLimitError,
} from '../guestServerResearch';
import { registerLiveSend, clearLiveSend } from '../../../components/GlobalChatSystem/liveSendRegistry';

import type {
  GlobalToolDefinition,
  StepContext,
  StepResult,
  ToolTheme,
  TokenUsageMetadata,
  ChatMessage,
} from '../types';

import {
  calculateTimeGap,
  evaluateTimeRelevance,
  formatTimeGapDisplay,
  detectTopicChangeSignals,
} from '../contextRelevance';
import { ENABLE_TIME_BASED_CONTEXT } from '../../../config/agenticWorkflowConfig';

// ============================================================================
// Error Classification Helper
// ============================================================================

/**
 * Classify a Perplexity/proxy error using status code, error code, and message text.
 * Uses numeric status and error codes for reliable detection, with string matching as fallback.
 */
export function classifyPerplexityError(error: unknown) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorObj = error as any;
  const errorStatus = errorObj?.status;
  const errorCode = typeof errorObj?.code === 'string' ? errorObj.code : '';

  // Status-less browser network failures (e.g. a transient blip on the long
  // Stage-2 / Stage-3 fetch) surface as `TypeError: Failed to fetch` with NO
  // .status. Without recognizing this, a single network hiccup was misclassified
  // as permanent/non-retryable and killed the whole deep-research run (the run
  // would silently "stop"). Treat it as transient so retry/degrade/resume kick in.
  // NOTE: a true client/timeout abort is re-mapped upstream to status 408
  // (perplexityClient TIMEOUT), so it is captured by isTimeout, not here.
  const isNetworkError = /failed to fetch|networkerror|network error|fetch failed|load failed|connection|econnreset|econnrefused|etimedout|socket hang up/i
    .test(errorMessage)
    || (errorObj?.name === 'TypeError' && !errorStatus);

  return {
    isTimeout: errorMessage.includes('timeout') || errorMessage.includes('abort')
      || errorMessage.includes('TimeoutError') || errorStatus === 408,
    isRateLimit: errorMessage.includes('429') || errorMessage.includes('rate limit')
      || errorStatus === 429,
    isServerError: errorMessage.includes('500') || errorMessage.includes('502')
      || errorMessage.includes('503') || errorMessage.includes('504')
      || (typeof errorStatus === 'number' && errorStatus >= 500 && errorStatus <= 599),
    isNetworkError,
    is401Error: errorMessage.includes('401') || errorMessage.includes('authentication')
      || errorMessage.includes('unauthorized') || errorStatus === 401,
    isApiKeyIssue: errorMessage.includes('API_KEY') || errorMessage.includes('PERPLEXITY_AUTH')
      || errorMessage.includes('API authentication failed')
      || errorCode === 'API_KEY_NOT_CONFIGURED' || errorCode === 'PERPLEXITY_AUTH_FAILED',
  };
}

/**
 * Whether a classified research failure is one the SERVER actually saw and
 * filed — i.e. whether the on-call sentence is TRUE for it.
 *
 * The proxies report every non-ok upstream response except client-input
 * rejections (functions/src/index.ts perplexityProxy/openRouterProxy call
 * reportPlatformIncident when the log class is not UPSTREAM_CLIENT_REJECTED),
 * so 5xx (UPSTREAM_SERVER_ERROR), 429 (UPSTREAM_RATE_LIMITED) and 401
 * (UPSTREAM_AUTH_FAILED) are each backed by a real filing.
 *
 * Deliberately excluded (deferred from #1233): `isTimeout` and
 * `isNetworkError` are client-side aborts — the request never completed at a
 * server that could report anything. `isApiKeyIssue` is excluded on its own
 * because its API_KEY_NOT_CONFIGURED case never leaves the browser; a key
 * failure that DID reach the proxy comes back 401 and sets `is401Error`,
 * which is covered. An unclassified failure is of unknown provenance, so it
 * stays silent too.
 */
export function researchFailureIsServerReported(cls: {
  isServerError: boolean;
  isRateLimit: boolean;
  is401Error: boolean;
}): boolean {
  return cls.isServerError || cls.isRateLimit || cls.is401Error;
}

// ============================================================================
// Model Display Name Helper
// ============================================================================

/**
 * Build display name from actual model used + task suffix.
 * Uses the model ID from tokenUsage to derive the codename,
 * so fallback models get correct names (e.g., "Echo Pro" not "Echo Deep Research").
 */
// buildModelDisplay: consolidated into ../modelDisplay (2026-06-11) — single
// recording-time codename builder shared by orchestrator + all tools.

/**
 * Build a caution-style (amber) retry notification for the chat stream.
 * Uses codenames instead of raw model IDs.
 */
function buildRetryNotification(
  modelId: string,
  stageName: string,
  attempt: number,
  maxRetries: number,
  retryAfterMs: number,
): { text: string; html: string } {
  let codename: string;
  if (/sonar-deep-research/i.test(modelId)) codename = 'Echo Deep Research';
  else if (/sonar-reasoning-pro/i.test(modelId)) codename = 'Echo Reasoning Pro';
  else if (/sonar-pro/i.test(modelId)) codename = 'Echo Pro';
  else if (/sonar/i.test(modelId)) codename = 'Echo';
  else codename = modelId;

  const seconds = Math.round(retryAfterMs / 1000);
  const text = `${codename} (${stageName}) — retrying (${attempt}/${maxRetries}) in ${seconds}s...`;
  // Include pendingShimmer + pendingSpin CSS references so the message stays rendered
  // as a floating pending overlay widget instead of dropping to a regular chat bubble.
  // Uses amber color scheme to visually distinguish retry state from normal pending.
  const html = `<div class="pending-widget-wrapper" style="position:relative;display:inline-flex;align-items:center;gap:12px;padding:10px 18px;background:linear-gradient(135deg,#fffbeb 0%,#fef3c7 50%,#fffbeb 100%);background-size:200% 100%;animation:pendingShimmer 3s ease-in-out infinite;border-radius:24px;border:1px solid #fde68a;font-family:'SF Pro Text',-apple-system,BlinkMacSystemFont,sans-serif;font-size:13px;font-weight:500;color:#92400e;letter-spacing:0.01em;user-select:none;" aria-live="polite" aria-busy="true" role="status"><div class="pending-spinner" style="width:16px;height:16px;border:2px solid rgba(146,64,14,0.2);border-top-color:#92400e;border-radius:50%;animation:pendingSpin 0.8s linear infinite;flex-shrink:0;"></div><span style="color:#92400e;font-weight:500;">${text}</span></div>`;
  return { text, html };
}

function safeBroadcastBridgeEvent(args: {
  event: 'thread.clarifying-question' | 'workflow.started' | 'workflow.progress' | 'workflow.completed' | 'workflow.failed';
  payload?: Record<string, unknown>;
}): void {
  try {
    broadcastBridgeEvent(args);
  } catch (error) {
    console.warn('[DeepResearch] bridge event emission failed:', args.event, error);
  }
}

function buildWorkflowSummary(reportContent: string, reportTitle?: string): string {
  const normalized = (reportContent || '').replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return reportTitle
      ? `Deep research completed: ${reportTitle}.`
      : 'Deep research completed successfully.';
  }

  const firstChunk = normalized.slice(0, 200).trim();
  const sentenceMatch = firstChunk.match(/^(.+?[.!?])(?:\s|$)/);
  const firstSentence = sentenceMatch?.[1]?.trim();

  if (firstSentence && firstSentence.length >= 40) {
    return firstSentence;
  }

  if (reportTitle) {
    return `${firstChunk}${firstChunk.endsWith('.') ? '' : '.'} Report: ${reportTitle}.`;
  }

  return `${firstChunk}${firstChunk.endsWith('.') ? '' : '.'}`;
}

// ============================================================================
// Conversation History Helper
// ============================================================================

/**
 * Format conversation history for inclusion in research context.
 *
 * CRITICAL PRINCIPLE: Default to NOT assuming history is relevant.
 * Only include full history if explicit continuity signals are present.
 *
 * Behavior:
 * - If current query has continuity signals ("that", "it", etc.) → include full history
 * - If NO continuity signals → include minimal context (2 messages) with warning note
 * - Time gaps further reduce the amount of context included
 *
 * @param history - Array of chat messages from sharedContext.messageHistory
 * @param maxMessages - Maximum number of messages to include (default: 10)
 * @param currentQuery - Current user query to check for continuity signals
 * @returns Formatted string with conversation context, or empty string if no relevant messages
 */
function formatConversationHistory(
  history: ChatMessage[] | undefined,
  maxMessages: number = 10,
  currentQuery?: string
): string {
  if (!history || history.length === 0) return '';

  // Check if current query has explicit continuity signals
  const hasContinuitySignals = currentQuery
    ? detectTopicChangeSignals(currentQuery).hasContinuitySignals
    : false;

  // Determine how much context to include based on signals
  let adjustedMax: number;
  let headerNote: string;

  if (hasContinuitySignals) {
    // User explicitly referenced prior context - include full history
    adjustedMax = maxMessages;
    headerNote = ' (user referenced prior context)';
    console.log('[DeepResearch] Continuity signals detected - including full conversation context');
  } else {
    // NO continuity signals - be VERY conservative (default behavior)
    // Only include last 2 messages as minimal context
    adjustedMax = 2;
    headerNote = ' (MINIMAL - may not be related to current query)';
    console.log('[DeepResearch] No continuity signals - including minimal context only');
  }

  // Time-based adjustment (secondary, reduces further if stale)
  if (ENABLE_TIME_BASED_CONTEXT && !hasContinuitySignals) {
    const timeGapMs = calculateTimeGap(history);
    const timeRelevance = evaluateTimeRelevance(timeGapMs);
    const timeGapDisplay = formatTimeGapDisplay(timeGapMs);

    if (timeRelevance.category === 'stale' || timeRelevance.category === 'low') {
      // Stale + no continuity signals = essentially ignore history
      adjustedMax = 1; // Just last message for minimal context
      headerNote = ` (STALE - ${timeGapDisplay}, likely unrelated)`;
      console.log('[DeepResearch] Stale + no continuity - reducing to single message context');
    }
  }

  // Filter to relevant messages (exclude pending, context-only, system tags, etc.)
  const relevantMessages = history
    .filter((msg) => {
      // Skip context-only messages (document analysis, etc.)
      if (msg.isContextOnly) return false;
      // Skip empty messages
      if (!msg.text || msg.text.trim() === '') return false;
      // Skip pending/temporary messages
      if (msg.sourceTag?.includes('pending')) return false;
      // Skip error messages
      if (msg.sourceTag?.includes('error')) return false;
      // Skip attachment read confirmations (already included as document analysis)
      if (msg.sourceTag?.includes('attachment_read')) return false;
      return true;
    })
    .slice(-adjustedMax);

  if (relevantMessages.length === 0) return '';

  const formatted = relevantMessages
    .map((msg) => {
      const role = msg.isUser ? 'User' : 'Assistant';
      // Truncate very long messages to avoid bloating the context
      const text = msg.text.length > 500 ? msg.text.slice(0, 500) + '...' : msg.text;
      return `${role}: ${text}`;
    })
    .join('\n\n');

  return `\n--- PRIOR CONVERSATION CONTEXT${headerNote} ---\n${formatted}\n--- END CONVERSATION CONTEXT ---\n`;
}

// ============================================================================
// Tool State
// ============================================================================

/**
 * State for the deep research tool.
 */
export interface DeepResearchState {
  /** Current stage of the workflow */
  stage: 'idle' | 'stage1' | 'stage1_retry' | 'stage1_confirm' | 'stage2' | 'stage2_retry' | 'stage3';
  /** Original user query */
  originalQuery: string;
  /** User clarifications provided */
  userClarifications: string[];
  /** Stage 1 findings */
  stage1Findings: string;
  /** Stage 1 questions */
  stage1Questions: string[];
  /** Stage 2 research brief */
  stage2Brief: string;
  /** Content from analyzed attachments */
  attachmentContent: string;
  /** Whether the workflow was cancelled */
  cancelled: boolean;
  /** Final report content */
  reportContent?: string;
  /** Report title */
  reportTitle?: string;
  /** Token usages from all stages */
  tokenUsages?: TokenUsageMetadata[];
}

// ============================================================================
// Tool Theme
// ============================================================================

const DEEP_RESEARCH_THEME: ToolTheme = {
  accent: '#1e40af',      // Blue 800
  accentBg: '#dbeafe',    // Blue 100
  border: '#3b82f6',      // Blue 500
  subtitle: '#475569',    // Slate 600
  muted: '#64748b',       // Slate 500
};

// ============================================================================
// Button IDs
// ============================================================================

// Button IDs for deepResearch tool actions
export const BUTTON_IDS = {
  skip: 'global-tool-deep-research-skip-btn',
  start: 'global-tool-deep-research-start-btn',
};

// ============================================================================
// Trigger Pattern
// ============================================================================

/**
 * Match "deep research [topic]" pattern.
 *
 * Phase 0 hardening: when the underlying NL parser declares a match, we
 * apply the in-clause negation guard against the deep-research anchor
 * keywords. Phrases like "I'm not asking for deep research, just a
 * quick answer" are then suppressed.
 */
const DEEP_RESEARCH_NEGATION_ANCHOR = /\b(deep\s+research|investigate|research)\b/i;

function matchDeepResearchTrigger(text: string): { matches: boolean; query?: string } {
  const detection = parseDeepResearchPrompt(text);
  if (detection.isDeepResearch) {
    if (hasNegationAround(text, DEEP_RESEARCH_NEGATION_ANCHOR)) {
      return { matches: false };
    }
    return { matches: true, query: detection.query };
  }
  return { matches: false };
}

// ============================================================================
// Tool Definition
// ============================================================================

export const deepResearchTool: GlobalToolDefinition<DeepResearchState> = {
  id: 'deep-research',
  name: 'Deep Research',
  description: 'Multi-stage research using Perplexity with document analysis',

  triggerPattern: matchDeepResearchTrigger,

  createInitialState: (): DeepResearchState => ({
    stage: 'idle',
    originalQuery: '',
    userClarifications: [],
    stage1Findings: '',
    stage1Questions: [],
    stage2Brief: '',
    attachmentContent: '',
    cancelled: false,
    reportContent: undefined,
    reportTitle: undefined,
    tokenUsages: undefined,
  }),

  steps: [
    // ========================================================================
    // Step 1: Initialize - Process query and attachments
    // ========================================================================
    {
      id: 'initialize',
      name: 'Initialize',
      activeDisplayName: 'preparing',

      execute: async (
        ctx: StepContext<DeepResearchState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const input = rawInput as { query?: string; isChainStep?: boolean; onStreamingStart?: () => void } | undefined;
        // Clean up the initial thinking indicator passed from useGlobalTools
        // to prevent spinner gap, but only once we start working.
        setTimeout(() => {
          if (input?.onStreamingStart) input.onStreamingStart();
        }, 50);

        const query = input?.query;

        if (!query) {
          ctx.messaging.showSpecialMessage({
            header: { title: 'topic required' },
            sections: [
              {
                type: 'text',
                content: 'please include a topic after "deep research".',
              },
            ],
            theme: 'blue',
          });

          return {
            status: 'completed',
            nextStep: null,
          };
        }

        safeBroadcastBridgeEvent({
          event: 'workflow.started',
          payload: {
            workflowKind: 'deep-research',
            title: query.slice(0, 120),
          },
        });

        // Check for attachments
        const attachments = ctx.triggerMessage.attachments;
        let documentAnalysis = '';

        // PHASE 11: Check if this is a chain step - if so, use previous workflow output
        if (input?.isChainStep) {
          console.log('[DeepResearch] Chain step - checking for previous workflow output');
          const previousOutput = ctx.sharedContext.lastTranslatableContentRef?.current ||
                                 ctx.sharedContext.lastTranslatableContent;
          if (previousOutput && previousOutput.content && previousOutput.content.trim()) {
            console.log('[DeepResearch] Using previous workflow output as context');
            documentAnalysis = previousOutput.content;
          } else if (previousOutput) {
            console.warn('[DeepResearch] Chain step: previous workflow output exists but has empty content — skipping');
          }
        }

        // Check orchestrator cache first (prevents redundant analysis)
        // IMPORTANT: Check ref first (sync) then state (async) due to React state timing
        const cachedDocumentContent = ctx.sharedContext.documentContentRef?.current ||
                                      ctx.sharedContext.documentContent;
        if (!documentAnalysis && cachedDocumentContent) {
          console.log('[DeepResearch] Using cached document analysis from orchestrator');
          console.log('[DeepResearch] Source:', ctx.sharedContext.documentContentRef?.current ? 'ref (sync)' : 'state (async)');
          documentAnalysis = cachedDocumentContent;
        } else if (!documentAnalysis && hasAnalyzableFiles(attachments) && attachments) {
          // No cache - analyze now
          const stream = ctx.messaging.showPending('reading attachment');

          try {
            const analysisResult = await analyzeDocumentsWithClaude({
              files: attachments,
              userQuery: query,
              signal: ctx.abortSignal,
            });
            documentAnalysis = analysisResult.analysis;

            // Cache for other tools
            ctx.sharedContext.setDocumentContent(documentAnalysis);

            // Use global helper for standardized attachment read confirmation with timing/info
            finalizeAttachmentReadComplete(stream, analysisResult, ctx.recordTransaction, {
              sourceTag: 'deep_research_attachment_read',
              transactionSummary: 'extracted doc evidence',
            });
          } catch (err) {
            console.error('[DeepResearch] Document analysis failed:', err);
            stream.finalize({
              payload: {
                kind: 'deep_research.error',
                toolId: 'deep-research',
                severity: 'warning',
                data: { errorMessage: 'document analysis failed', phase: 'attachment_read' },
                summary: 'deep research: document analysis failed; proceeding with query text only',
              },
              sourceTag: 'attachment_read_error',
            });
          }
        }

        ctx.setState({
          stage: 'stage1',
          originalQuery: query,
          attachmentContent: documentAnalysis,
          tokenUsages: [],
        });

        return {
          status: 'completed',
          carryForward: {
            query,
            documentAnalysis,
          },
        };
      },
    },

    // ========================================================================
    // Step 2: Qualify - Stage 1 qualification analysis
    // ========================================================================
    {
      id: 'qualify',
      name: 'Qualify Query',
      activeDisplayName: 'assessing',

      execute: async (
        ctx: StepContext<DeepResearchState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        // If we're being resumed from stage1_confirm with user input, proceed to research step
        // This prevents the qualify step from re-running the API call and looping
        if (ctx.state.stage === 'stage1_confirm' && ctx.userInput !== undefined) {
          // Note: The video script tool now resets the deep research runtime when it starts,
          // so this code path should not be reached when video script is active.
          // The resetToolRuntime('deep-research') call in videoScript.ts clears our isAwaitingInput flag.

          // Clear persisted workflow state — user is resuming or cancelling
          try { localStorage.removeItem('prest0_dr_workflow_state'); } catch {}
          // Also clear Firestore (cross-device)
          try {
            const user = getAuth().currentUser;
            if (user) {
              deleteDoc(doc(getFirestore(), 'users', user.uid, 'workflow_state', 'deep_research'))
                .catch(() => {});
            }
          } catch {}

          // Check for cancel command
          if (isCancelCommand(ctx.userInput)) {
            ctx.messaging.showSpecialMessage({
              header: { title: 'deep research cancelled' },
              sections: [{ type: 'text', content: 'returning to normal chat.' }],
              theme: 'blue',
            });
            ctx.setState({ stage: 'idle', cancelled: true });
            return { status: 'completed', nextStep: null };
          }

          // Check for skip command (skip sentinel / "skip" / "skip clarification"
          // / button click empty string). 2026-05-28: use the shared
          // isSkipClarificationSentinel helper so the avatar's skip request
          // (which previously contained the phrase "Deep Research" and got
          // misrouted as a brand-new workflow trigger) is recognized as a skip
          // and resumes THIS parked workflow with empty clarifications.
          const isSkip =
            ctx.userInput.trim() === '' ||
            isSkipClarificationSentinel(ctx.userInput);

          // User is providing clarifications - proceed to research step
          return {
            status: 'completed',
            carryForward: {
              query: ctx.state.originalQuery,
              documentAnalysis: ctx.state.attachmentContent,
              stage1Findings: ctx.state.stage1Findings,
              clarifyingQuestions: ctx.state.stage1Questions,
              userClarificationText: isSkip ? '' : ctx.userInput,
            },
          };
        }

        // If we're being resumed from stage1_retry (user clicked "try again" after Stage 1 failure),
        // reset stage and fall through to re-execute the Stage 1 API call
        if (ctx.state.stage === 'stage1_retry') {
          console.log('[DeepResearch] Retrying Stage 1 assessment...');
          ctx.setState({ stage: 'stage1' });
          // Fall through to main execution path below
        }

        const input = rawInput as {
          query: string;
          documentAnalysis?: string;
          skipClarification?: boolean;  // Orchestrator can suggest skipping clarification
        } | undefined;
        const query = input?.query || ctx.state.originalQuery;
        const documentAnalysis = input?.documentAnalysis || ctx.state.attachmentContent;
        // Deterministic no-clarify backstop (2026-08-07, July 8 feature test): the
        // orchestrator LLM authors input.skipClarification, but an explicit user
        // instruction ("do not ask clarifying questions" / "proceed with available
        // context") must not depend on prompt compliance. Check the tool query AND the
        // MESSAGE THAT TRIGGERED THIS RUN (ctx.triggerMessage — raw, unrewritten, so the
        // orchestrator dropping the instruction from its query rewrite can't hide it).
        // Deliberately NOT the latest history message: in a chained workflow that could
        // be an instruction addressed to a PREVIOUS step (review fix).
        const triggerText = typeof ctx.triggerMessage?.text === 'string' ? ctx.triggerMessage.text : '';
        const explicitNoClarify = detectNoClarifyInstruction([query ?? '', triggerText].join('\n'));
        if (explicitNoClarify && !input?.skipClarification) {
          console.log('[DeepResearch] Explicit no-clarify instruction detected in user text - skipping clarification interstitial');
        }
        const skipClarification = (input?.skipClarification ?? false) || explicitNoClarify;

        if (!query) {
          return {
            status: 'failed',
            error: new Error('No query provided'),
          };
        }

        // Build Stage 1 query with conversation history for context
        // This ensures Stage 1 qualification has awareness of prior conversation
        // CRITICAL: Pass query to detect continuity signals ("it", "that", etc.)
        const conversationContext = formatConversationHistory(ctx.sharedContext?.messageHistory, 10, query);

        const stage1Query = [
          query,
          conversationContext,
          documentAnalysis ? `\n--- DOCUMENT ANALYSIS (from attached files) ---\n${documentAnalysis}` : '',
        ].filter(Boolean).join('\n');

        const stream = ctx.messaging.showPending('assessing');

        // T7/T8 fix (2026-05-11): mirror the user's input language across
        // the entire deep-research workflow on the global-tools orchestrator
        // path (the path that fires from prest0.ai homepage chat). Detect from
        // the raw `query` so document analysis appended for context doesn't
        // override the user's actual input language.
        const drInputLanguage = detectInputLanguage(query);
        const stage1SystemPromptLocalized =
          buildLanguageDirective(drInputLanguage) + STAGE1_SYSTEM_PROMPT;

        try {
          const result = await runPerplexityResearch({
            query: stage1Query,
            systemPrompt: stage1SystemPromptLocalized,
            // D14 slice 3b: the route name is the routing input — the server
            // stamps the SSOT ladder + effort (mode:'task' on perplexityProxy).
            // model/reasoningEffort below feed ONLY the marked deploy-skew
            // fallback inside perplexityClient and retire with it.
            route: 'deep-research-qualification',
            model: PERPLEXITY_DEEP_RESEARCH_STAGE1_MODEL,
            reasoningEffort: PERPLEXITY_DEEP_RESEARCH_STAGES_1_2_REASONING_EFFORT,
            searchContextSize: PERPLEXITY_SEARCH_CONTEXT_SIZE,
            // Use direct Cloud Function URL to bypass Firebase Hosting's 60s rewrite timeout.
            // sonar-reasoning-pro with high reasoning effort routinely takes 60-120s.
            proxyUrl: PERPLEXITY_PROXY_URL_DIRECT,
            // Stage 1 is a qualification step — aggressive timeouts to prevent 10+ min hangs.
            // On justiceisurright, we allow more retries to prevent 429 rate limit drops.
            timeout: 90000,
            maxRetries: (typeof window !== 'undefined' && window.location.pathname.includes('justiceisurright')) ? 4 : 2,
            skipFallback: true,
            onRetry: (attempt, maxRetries, _error, retryAfterMs) => {
              // Show retry notification on every attempt so user sees progress
              stream.update({
                text: `retry ${attempt}`,
                html: getPendingWidgetHtml(`retry ${attempt}`),
              });
            },
            onRetryStart: (attempt, maxRetries) => {
              stream.update({
                text: `retrying assessment... (attempt ${attempt + 1}/${maxRetries})`,
                html: getPendingWidgetHtml(`retrying assessment (attempt ${attempt + 1}/${maxRetries})`),
              });
            },
          });

          // Store token usage for recording AFTER stream.finalize() (fixes timing issue)
          const stage1TokenUsage = result.tokenUsage;
          if (stage1TokenUsage) {
            console.log('[DeepResearch] Stage 1 tokenUsage.model:', stage1TokenUsage.model);
            // ENRICHMENT: Estimate reasoning tokens from think blocks if API didn't provide them
            const stage1RawText = result.text || '';
            const stage1ThinkBlocks: string[] = stage1RawText.match(/<think>[\s\S]*?<\/think>/gi) || [];
            const stage1ThinkChars = stage1ThinkBlocks.reduce((sum: number, b: string) => sum + b.length, 0);
            if (stage1TokenUsage.reasoningTokens === undefined && stage1ThinkChars > 0) {
              stage1TokenUsage.reasoningTokens = Math.ceil(stage1ThinkChars / 4);
            }
            // Citation count from API response
            if (stage1TokenUsage.citationTokens === undefined && result.citations && result.citations.length > 0) {
              stage1TokenUsage.citationTokens = result.citations.length * 50;
            }
          }

          // Parse Stage 1 response
          let requiresDeep = true;
          let clarifyingQuestions: string[] = [];
          let initialContext = '';
          let simpleAnswer = '';
          let reasoning = '';

          const parsed = parseStage1Json(result.text || '');
          if (parsed) {
            requiresDeep = parsed.requiresDeepResearch;
            clarifyingQuestions = parsed.clarifyingQuestions || [];
            initialContext = parsed.initialContext || '';
            simpleAnswer = parsed.simpleAnswer || '';
            reasoning = parsed.reasoning || '';

            // Log question quality for debugging
            if (clarifyingQuestions.length > 0) {
              const genericPatterns = ['specific outcome', 'any deadlines', 'additional context', 'what is your goal'];
              const seemsGeneric = clarifyingQuestions.some(q =>
                genericPatterns.some(p => q.toLowerCase().includes(p))
              );
              if (seemsGeneric) {
                console.warn('[Stage1] Questions appear generic - prompt may need adjustment:', clarifyingQuestions);
              } else {
                console.log('[Stage1] Contextual questions received:', clarifyingQuestions.length);
              }
            }
          }

          // Layer 2: If AI returned empty initialContext but deep research needed,
          // generate query-based initial context immediately to ensure we never show generic text
          if (!initialContext && requiresDeep) {
            console.warn('[Stage1] AI returned empty initialContext, generating query-based summary');
            initialContext = generateQueryBasedInitialContext(stage1Query);
          }

          // Simple question - provide quick answer
          if (!requiresDeep && simpleAnswer) {
            const reasoningNote = reasoning
              ? `<p style="margin-top:8px;color:#64748b;font-size:0.85rem;font-style:italic;">${reasoning}</p>`
              : '';

            const quickAnswerCtaHtml = buildDeepResearchQuickAnswerCtaHtml(ctx.triggerMessage.text || query);

            stream.finalize({
              text: simpleAnswer,
              html: `<div style="display:flex;flex-direction:column;gap:8px;margin:4px 0;">
                <strong style="color:#1e40af;font-size:0.95rem;">quick answer</strong>
                <div style="color:#0f172a;font-size:0.9rem;line-height:1.5;">${simpleAnswer}</div>
                ${reasoningNote}
                ${quickAnswerCtaHtml}
              </div>`,
              sourceTag: 'deep_research_quick_answer',
              tokenUsage: stage1TokenUsage ? { ...stage1TokenUsage, summary: 'quick answer' } : undefined,
            });

            // Record transaction AFTER message display (fixes timing issue)
            if (stage1TokenUsage) {
              ctx.recordTransaction({
                tokenUsage: stage1TokenUsage,
                source: 'agenticWorkflows',
                modelOverride: buildModelDisplay(stage1TokenUsage, '', PERPLEXITY_DEEP_RESEARCH_STAGE1_DISPLAY),
                summary: 'research assessment',
              });
            }

            return {
              status: 'completed',
              nextStep: null,
            };
          }

          // Deep research is needed - ensure we have clarifying questions
          if (clarifyingQuestions.length === 0) {
            console.warn('[Stage1] API returned no clarifying questions, generating contextual fallbacks');
            // Extract topic from query for more contextual fallback questions
            const topicMatch = query.match(/^(?:deep\s+research\s+)?(.+)/i);
            const topic = topicMatch ? topicMatch[1].trim() : 'this topic';

            clarifyingQuestions = [
              `What specific aspect of ${topic} is most important for your situation?`,
              `Are there any pending deadlines, court dates, or filing windows related to ${topic}?`,
              `What is your current immigration status, and how does it relate to ${topic}?`,
            ];
          }

          // Only use initialContext for display (reasoning is kept internal/for logging)
          let displayContext = initialContext?.trim() || '';

          // Layer 3: Final fallback using query-based generation
          // This should rarely execute after Layer 2 catches empty initialContext early
          if (!displayContext) {
            console.warn('[DeepResearch] displayContext still empty after Layer 2 - using query-based generation');
            displayContext = generateQueryBasedInitialContext(stage1Query);
          }



          // Store Stage 1 findings in shared context so video script tool can access them
          const stage1TranslatableData = {
            content: `${reasoning}\n\n${initialContext}`,
            title: query.slice(0, 100) || 'Research Findings',
            timestamp: new Date(),
            source: 'research' as const,
          };
          ctx.sharedContext.setLastTranslatableContent(stage1TranslatableData);
          ctx.sharedContext.lastTranslatableContentRef.current = stage1TranslatableData;

          // Store in workflow output store for button context passing
          // This ensures content is available when user clicks buttons
          const workflowOutputId = storeWorkflowOutput({
            id: `dr-stage1-${Date.now()}`,
            content: `${reasoning}\n\n${initialContext}`,
            title: query.slice(0, 100) || 'Research Findings',
            timestamp: new Date(),
            source: 'research',
            metadata: {
              originalQuery: query,
              documentAnalysis: documentAnalysis || undefined,
              clarifyingQuestions,
            },
            tokenUsages: stage1TokenUsage ? [{ ...stage1TokenUsage, summary: 'topic assessment' }] : undefined,
          });

          // Parse displayContext to get findings and howAnswersHelp separately
          const contextParsed = parseInitialContextSections(displayContext);
          const initialFindings = contextParsed?.preliminaryFindings || displayContext;
          const howAnswersHelp = contextParsed?.howQuestionsHelp || null;

          // Sync stage1 findings to lastTranslatableContent so video script can access it
          // This bridges the gap between tool internal state and sharedContext
          const stage1Content = `${reasoning}\n\n${initialContext}`;
          const combinedContent = documentAnalysis
            ? `${stage1Content}\n\n--- Document Analysis ---\n${documentAnalysis}`
            : stage1Content;

          const translatableData = {
            content: combinedContent,
            title: query.slice(0, 100) || 'Research Findings',
            timestamp: new Date(),
            source: 'research' as const,
          };
          ctx.sharedContext.setLastTranslatableContent(translatableData);
          ctx.sharedContext.lastTranslatableContentRef.current = translatableData;

          // Check if orchestrator requested skipping clarification
          if (skipClarification) {
            console.log('[DeepResearch] Orchestrator requested skipClarification - proceeding directly to research');

            safeBroadcastBridgeEvent({
              event: 'workflow.progress',
              payload: {
                stage: 'stage2',
                message: 'Skipping clarifying questions, proceeding directly to deep research',
              },
            });

            // Finalize with empty message to avoid visual clutter
            stream.finalize({
              text: '',
              html: '',
              sourceTag: 'deep_research_qualification_skipped',
              tokenUsage: stage1TokenUsage ? { ...stage1TokenUsage, summary: 'topic assessment' } : undefined,
            });

            // Record transaction AFTER message display (fixes timing issue)
            if (stage1TokenUsage) {
              ctx.recordTransaction({
                tokenUsage: stage1TokenUsage,
                source: 'agenticWorkflows',
                modelOverride: buildModelDisplay(stage1TokenUsage, '', PERPLEXITY_DEEP_RESEARCH_STAGE1_DISPLAY),
                summary: 'research assessment',
              });
            }

            // Update state and proceed directly to research step
            ctx.setState({
              stage: 'stage2',
              stage1Findings: `${reasoning}\n\n${initialContext}`,
              stage1Questions: clarifyingQuestions,
              tokenUsages: [
                ...(ctx.state.tokenUsages || []),
                ...(stage1TokenUsage ? [{ ...stage1TokenUsage, summary: 'topic assessment' }] : []),
              ],
            });

            // Proceed directly to research step
            return {
              status: 'completed',
              carryForward: {
                query,
                documentAnalysis,
                stage1Findings: `${reasoning}\n\n${initialContext}`,
                clarifyingQuestions,
                userClarificationText: '', // No user clarifications since we skipped
              },
            };
          }

          // Generate qualification message with standard template formatting
          // Pass workflowOutputId so buttons can carry context to next workflow
          const { mainHtml, buttonsHtml } = formatDeepResearchQualificationHtml(
            initialFindings,
            howAnswersHelp,
            clarifyingQuestions,
            BUTTON_IDS.skip,
            VIDEO_SCRIPT_BUTTON_IDS.videoScript,
            workflowOutputId,
            drInputLanguage,
            // FINDINGS-FIRST REDESIGN (2026-08-19): one button on every
            // clarify surface — the card's helper line owns answer-below.
            { hidden: true },
          );

          stream.finalize({
            text: drInputLanguage === 'es' ? 'hallazgos iniciales — unas preguntas rápidas' : 'initial findings — a few quick questions',
            html: mainHtml,  // Main content without buttons
            workflowActionButtonsHtml: buttonsHtml,  // Buttons separately for selection state processing
            sourceTag: 'deep_research_qualification',
            tokenUsage: stage1TokenUsage ? { ...stage1TokenUsage, summary: 'topic assessment' } : undefined,
          });

          safeBroadcastBridgeEvent({
            event: 'thread.clarifying-question',
            payload: {
              // `findings` retained for backward-compat; `stage1Summary` is the
              // field the voice-server narration reads to summarize the initial
              // findings OUT LOUD before reading the first clarifying question.
              // (2026-05-28: avatar was skipping the summary because this field
              // was missing — voice-server reads payload.stage1Summary.)
              findings: `${reasoning}\n\n${initialContext}`,
              stage1Summary: `${reasoning}\n\n${initialContext}`,
              questions: clarifyingQuestions,
              questionCount: clarifyingQuestions.length,
              options: ['answer', 'skip', 'skip-all'],
              inputLanguage: drInputLanguage,
            },
          });

          // Force immediate Firestore sync so the clarifying questions + buttons
          // survive sign-out/device-switch. Without this, the background sync might
          // not fire before the user leaves, causing buttons to disappear on Device B.
          setTimeout(() => {
            try { window.dispatchEvent(new Event('global-chat-force-sync')); } catch {}
          }, 500); // Small delay for React state to settle after finalize

          // Record transaction AFTER message display (fixes timing issue)
          if (stage1TokenUsage) {
            ctx.recordTransaction({
              tokenUsage: stage1TokenUsage,
              source: 'agenticWorkflows',
              modelOverride: buildModelDisplay(stage1TokenUsage, '', 'Echo Reasoning Pro'),
              summary: 'research assessment',
            });
          }

          // Update state
          ctx.setState({
            stage: 'stage1_confirm',
            stage1Findings: `${reasoning}\n\n${initialContext}`,
            stage1Questions: clarifyingQuestions,
            tokenUsages: [
              ...(ctx.state.tokenUsages || []),
              ...(stage1TokenUsage ? [{ ...stage1TokenUsage, summary: 'topic assessment' }] : []),
            ],
          });

          // Persist workflow state to localStorage so the user can resume
          // after page reload / tab close. The "skip to deep research" button
          // persists in chat history, but the runtime state is in-memory only.
          // Persist workflow state for resumption (localStorage + Firestore for cross-device)
          const workflowStatePayload = {
            stage: 'stage1_confirm',
            originalQuery: query,
            stage1Findings: `${reasoning}\n\n${initialContext}`,
            stage1Questions: clarifyingQuestions,
            attachmentContent: documentAnalysis || '',
            savedAt: Date.now(),
          };
          try {
            localStorage.setItem('prest0_dr_workflow_state', JSON.stringify(workflowStatePayload));
            // Durability audit rank #13 (2026-07-13): stamp the checkpoint's
            // owning uid (sibling key — checkpoint wire-format unchanged for
            // in-flight resumes) so a different account signing into this
            // browser can never restore another account's clarifying state.
            try {
              const ownerUid = getAuth().currentUser?.uid;
              if (ownerUid) localStorage.setItem('prest0_dr_workflow_state_owner', ownerUid);
            } catch { /* best-effort */ }
            console.log('[DeepResearch] Persisted stage1_confirm state to localStorage');
          } catch (e) {
            console.warn('[DeepResearch] Failed to persist workflow state to localStorage:', e);
          }
          // Write to Firestore for cross-device resume — AWAIT to guarantee persistence
          // before the user can switch devices or refresh. Without awaiting, the write
          // may be in-flight when the user navigates away, leaving Device B with no state.
          try {
            const user = getAuth().currentUser;
            if (user) {
              const db = getFirestore();
              await setDoc(doc(db, 'users', user.uid, 'workflow_state', 'deep_research'), {
                ...workflowStatePayload,
                savedAt: serverTimestamp(),
              }, { merge: true });
              console.log('[DeepResearch] Persisted stage1_confirm state to Firestore (cross-device)');
            }
          } catch (e) {
            console.warn('[DeepResearch] Firestore persist failed (non-fatal):', e);
          }

          // Wait for user clarifications
          return {
            status: 'awaiting-input',
            carryForward: {
              query,
              documentAnalysis,
              stage1Findings: `${reasoning}\n\n${initialContext}`,
              clarifyingQuestions,
            },
          };
        } catch (error) {
          // Classify error using numeric status, error code, and message text
          const { isTimeout, isRateLimit, isServerError, isNetworkError, is401Error, isApiKeyIssue } = classifyPerplexityError(error);

          let userFriendlyMessage = 'An error occurred while assessing your research topic. Please try again.';
          if (is401Error || isApiKeyIssue) {
            userFriendlyMessage = 'Research service is currently unavailable due to configuration issues. Please contact support.';
          } else if (isTimeout) {
            userFriendlyMessage = 'The assessment request timed out. Please try a simpler query or try again later.';
          } else if (isRateLimit) {
            userFriendlyMessage = 'The research provider is currently facing high demand. Please try again in a moment.';
          } else if (isServerError) {
            userFriendlyMessage = 'The research service is temporarily unavailable. Please try again in a moment.';
          } else if (isNetworkError) {
            userFriendlyMessage = 'The connection was interrupted. Please try again in a moment.';
          }
          // Owner directive 2026-08-13: platform errors carry the on-call
          // notice — but ONLY the classes the proxy actually reported
          // server-side (deferred from #1233). A client-side timeout or
          // network abort never reached a server that could file anything.
          if (researchFailureIsServerReported({ isServerError, isRateLimit, is401Error })) {
            userFriendlyMessage = withOncallNotice(userFriendlyMessage);
          }

          // For retryable errors (server errors, rate limits, client timeouts),
          // offer retry via awaiting-input instead of permanently failing the
          // workflow. This lets the user click "try again" without having to
          // restart the entire deep research workflow from scratch.
          // IMPORTANT: Exclude auth/config errors — these are permanent and retrying loops forever.
          if ((isServerError || isRateLimit || isTimeout || isNetworkError) && !(is401Error || isApiKeyIssue)) {
            const retryButtonHtml = `<div style="margin-top:12px;">
              <button data-button-id="${BUTTON_IDS.skip}" style="padding:8px 16px;background:#1e40af;color:white;border:none;border-radius:6px;cursor:pointer;font-size:0.85rem;">try again</button>
            </div>`;

            stream.finalize({
              text: userFriendlyMessage,
              html: `<p>${userFriendlyMessage}</p>`,
              payload: {
                kind: 'deep_research.error',
                toolId: 'deep-research',
                severity: 'error',
                data: { errorMessage: userFriendlyMessage, phase: 'stage1', retryable: true },
                summary: 'deep research: stage1 failed (retryable)',
              },
              workflowActionButtonsHtml: retryButtonHtml,
              sourceTag: 'research_error_retryable',
            });

            ctx.setState({ stage: 'stage1_retry' });

            return {
              status: 'awaiting-input',
              carryForward: {
                query,
                documentAnalysis,
              },
            };
          }

          // Non-retryable errors: fail permanently
          stream.finalize({
            text: userFriendlyMessage,
            html: `<p>${userFriendlyMessage}</p>`,
            payload: {
              kind: 'deep_research.error',
              toolId: 'deep-research',
              severity: 'error',
              data: { errorMessage: userFriendlyMessage, phase: 'stage1', retryable: false },
              summary: 'deep research: stage1 failed (non-retryable)',
            },
            sourceTag: 'research_error',
          });

          safeBroadcastBridgeEvent({
            event: 'workflow.failed',
            payload: {
              stage: 'stage1',
              message: userFriendlyMessage,
            },
          });

          return {
            status: 'failed',
            error: new Error(`Unable to qualify research: ${formatPerplexityError(error)}`),
          };
        }
      },
    },

    // ========================================================================
    // Step 3: Research - Stage 2 (brief) + Stage 3 (execution)
    // ========================================================================
    {
      id: 'research',
      name: 'Execute Research',
      activeDisplayName: 'deep researching',

      execute: async (
        ctx: StepContext<DeepResearchState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const input = rawInput as {
          query: string;
          documentAnalysis?: string;
          stage1Findings: string;
          clarifyingQuestions: string[];
        } | undefined;

        // Handle user input from awaiting-input state
        let userClarificationText = '';

        if (ctx.userInput !== undefined) {
          // Check for cancel command
          if (isCancelCommand(ctx.userInput)) {
            ctx.messaging.showSpecialMessage({
              header: { title: 'deep research cancelled' },
              sections: [{ type: 'text', content: 'returning to normal chat.' }],
              theme: 'blue',
            });
            ctx.setState({ stage: 'idle', cancelled: true });
            return {
              status: 'completed',
              nextStep: null,
            };
          }

          // Handle retry from server error (stage2_retry)
          // When user clicks "try again" after a 500 error, re-execute the research step
          if (ctx.state.stage === 'stage2_retry') {
            // Clear the retry state — proceed with normal research flow below
            userClarificationText = '';
          } else {
            userClarificationText = ctx.userInput;
          }
        }

        const query = input?.query || ctx.state.originalQuery;
        const documentAnalysis = input?.documentAnalysis || ctx.state.attachmentContent;
        const stage1Findings = input?.stage1Findings || ctx.state.stage1Findings;
        const clarifyingQuestions = input?.clarifyingQuestions || ctx.state.stage1Questions;

        const allClarifications = userClarificationText
          ? [...ctx.state.userClarifications, userClarificationText]
          : ctx.state.userClarifications;

        // Check for additional attachments in the user's clarification message
        let clarificationDocAnalysis = '';
        const userAttachments = ctx.userAttachments;

        if (hasAnalyzableFiles(userAttachments) && userAttachments) {
          const attachStream = ctx.messaging.showPending('reading additional attachment');

          try {
            const analysisResult = await analyzeDocumentsWithClaude({
              files: userAttachments,
              userQuery: `Context: User is providing additional documents for research on: ${query}`,
              signal: ctx.abortSignal,
            });
            clarificationDocAnalysis = analysisResult.analysis;

            // Use global helper for standardized attachment read confirmation with timing/info
            finalizeAttachmentReadComplete(attachStream, analysisResult, ctx.recordTransaction, {
              sourceTag: 'deep_research_additional_attachment_read',
              transactionSummary: 'read follow-up documents',
            });
          } catch (err) {
            console.error('[DeepResearch] Clarification doc analysis failed:', err);
            attachStream.finalize({
              text: 'additional document analysis failed — proceeding with existing context.',
              html: generateSpecialMessageTemplate({
                header: { title: 'additional document analysis failed', icon: '⚠' },
                sections: [{ type: 'text', content: 'proceeding with existing context.' }],
                theme: 'amber',
              }),
              sourceTag: 'attachment_read_error',
            });
          }
        }

        // Combine all document analysis
        const combinedAttachmentContent = documentAnalysis
          ? clarificationDocAnalysis
            ? `${documentAnalysis}\n\n--- ADDITIONAL DOCUMENTS (from clarification) ---\n${clarificationDocAnalysis}`
            : documentAnalysis
          : clarificationDocAnalysis;

        ctx.setState({
          stage: 'stage3',
          userClarifications: allClarifications,
          attachmentContent: combinedAttachmentContent,
        });

        // Start streaming message
        const stream = ctx.messaging.showPending('deep researching');

        // Tracks whether stream.finalize() has run. Used by both the normal
        // finalize path and the 120s safety-net watchdog below to prevent
        // the UI from getting stuck on "deep researching" forever. Declared
        // at this outer scope so the catch block can mark finalized too.
        let finalized = false;
        let forceFinalizeTimeoutId: ReturnType<typeof setTimeout> | undefined;
        const clearForceFinalizeWatchdog = () => {
          if (forceFinalizeTimeoutId) {
            clearTimeout(forceFinalizeTimeoutId);
            forceFinalizeTimeoutId = undefined;
          }
        };

        // ====================================================================
        // Point 6 (Option B) — SERVER-SIDE DURABLE GUEST PATH
        // ====================================================================
        // On the unauthenticated guest/homepage surface, run deep research
        // SERVER-SIDE so it survives a browser hard-refresh. The host wires
        // `ctx.sharedContext.guestServerResearch.enabled = true` only when there
        // is NO migrated prest0n session. The migrated/paid path never reaches
        // this legacy tool (it uses prest0nRunTurn), so this branch is additive
        // and cannot change that behavior.
        //
        // Flow: POST guestStartDeepResearch -> persist serverTaskId on this
        // message (refresh-survival anchor) -> poll guestPollTask (stage widget)
        // -> finalize the SAME stream with the server memo + serialized PDF/DOCX.
        const guestServer = ctx.sharedContext?.guestServerResearch;
        if (guestServer?.enabled) {
          try {
            const guestLanguage = detectInputLanguage(query) === 'es' ? 'es' : 'en';
            stream.update({
              html: getPendingWidgetHtml('starting deep research'),
            });

            // PRE-FLIGHT STAMP (stamp-race fix, 2026-07-10): mint the task id
            // NOW and stamp it BEFORE the start request. Previously the stamp
            // landed only after the 202 — a refresh in that window orphaned
            // the widget ("Interrupted") even though the server task started.
            const clientTaskId = mintGuestClientTaskId();
            // Durability audit rank #11 (2026-07-13): the pre-flight stamp
            // wakes the guest resume effect before polling starts — register
            // the live send FIRST so the S1 hijack guard covers guests too
            // (previously registerLiveSend's only call site was authed).
            try {
              registerLiveSend(clientTaskId);
            } catch { /* guard must never block the send */ }
            try {
              guestServer.persistServerTaskId?.(stream.id, clientTaskId);
            } catch (persistErr) {
              console.warn('[DeepResearch] guest pre-flight persistServerTaskId failed:', persistErr);
            }

            const { taskId } = await startGuestDeepResearch({
              prompt: query,
              language: guestLanguage,
              documentContent: combinedAttachmentContent || undefined,
              signal: ctx.abortSignal,
              clientTaskId,
            });

            // Re-stamp with the server's id when it differs (older backend
            // that ignores clientTaskId) so re-attach polls the real task.
            if (taskId !== clientTaskId) {
              try {
                registerLiveSend(taskId);
              } catch { /* guard must never block the send */ }
              try {
                guestServer.persistServerTaskId?.(stream.id, taskId);
              } catch (persistErr) {
                console.warn('[DeepResearch] guest persistServerTaskId failed (re-attach may not survive refresh):', persistErr);
              }
            }

            const pollResult = await pollGuestTaskUntilDone(taskId, {
              signal: ctx.abortSignal,
              onStage: (s) => {
                if (s?.label) {
                  stream.update({ html: getPendingWidgetHtml(s.label) });
                }
              },
            }).finally(() => {
              // Live send settled (any outcome) — release the hijack guard so
              // legitimate refresh-recovery can claim the task id later.
              try {
                clearLiveSend(clientTaskId);
                clearLiveSend(taskId);
              } catch { /* never block settle */ }
            });

            if (pollResult.status === 'complete' && pollResult.result) {
              const reportContent = ensureReferencesSection(
                stripThinkingBlocks(pollResult.result.report || ''),
              );
              const reportTitle =
                (pollResult.result.title || '').trim() ||
                generateConciseTitle(reportContent, query) ||
                'Deep Research Report';
              const researchHtml = formatResearchToHtml(reportContent);
              const serverAttachments = Array.isArray(pollResult.result.attachments)
                ? pollResult.result.attachments
                : [];


              finalized = true;
              stream.finalize({
                text: reportContent,
                html: researchHtml,
                sourceTag: 'research_result',
                // Server already uploaded PDF/DOCX to Storage; pass the signed
                // URLs straight through as serialized attachments (the
                // SerializedAttachment shape matches the server payload).
                attachmentsSerialized: serverAttachments.length > 0 ? serverAttachments : undefined,
                workflowOutput: {
                  type: 'research_report',
                  content: reportContent,
                  title: reportTitle,
                  timestamp: new Date(),
                  sourceWorkflow: 'deep-research',
                },
              });
              clearForceFinalizeWatchdog();

              // Share downstream + persist into tool state (parity w/ local path).
              const reportData = { content: reportContent, title: reportTitle, timestamp: new Date() };
              ctx.sharedContext.setLastResearchReport(reportData);
              ctx.sharedContext.lastResearchReportRef.current = reportData;
              const translatableData = {
                content: reportContent,
                title: reportTitle,
                timestamp: new Date(),
                source: 'research' as const,
              };
              ctx.sharedContext.setLastTranslatableContent(translatableData);
              ctx.sharedContext.lastTranslatableContentRef.current = translatableData;

              ctx.setState({ stage: 'idle', reportContent, reportTitle });
              safeBroadcastBridgeEvent({
                event: 'workflow.completed',
                payload: { summary: buildWorkflowSummary(reportContent, reportTitle), reportTitle },
              });

              return {
                status: 'completed',
                output: { content: reportContent, title: reportTitle },
              };
            }

            // NON-DESTRUCTIVE EXPIRY (2026-07-10): 'timeout' means the SERVER
            // task is still running — the live poll just outlasted its wall
            // clock. Do NOT finalize an error card over a live run: keep the
            // pending widget (serverTaskId already stamped), and let the
            // hydrate-resume machinery in useGlobalChat re-attach — its
            // message-scan effect picks the widget up on the next state
            // change, and a refresh re-attaches via the stamp.
            if (pollResult.status === 'timeout') {
              stream.update({
                html: getPendingWidgetHtml(
                  guestLanguage === 'es'
                    ? 'la investigación sigue en curso — se actualizará automáticamente'
                    : 'research is still running — this will update automatically',
                ),
              });
              clearForceFinalizeWatchdog();
              ctx.setState({ stage: 'idle' });
              return { status: 'completed', nextStep: null };
            }

            // status === 'error' | 'not_found'.
            finalized = true;
            const guestErrText =
              guestLanguage === 'es'
                ? 'La investigación no se pudo completar. Por favor, inténtalo de nuevo.'
                : 'The research could not be completed. Please try again.';
            // 2026-06 reliability overhaul: pollResult.error is a raw
            // server-provided string — run it through the raw-dump filter so
            // JSON/stack payloads never render into the card (short human
            // notices like the client-built timeout message still pass).
            const safeGuestPollError = sanitizeServerErrorNotice(pollResult.error);
            stream.finalize({
              text: guestErrText,
              html: generateSpecialMessageTemplate({
                header: { title: guestLanguage === 'es' ? 'investigación incompleta' : 'research incomplete', icon: '⚠' },
                sections: [{ type: 'text', content: safeGuestPollError || guestErrText }],
                theme: 'amber',
              }),
              sourceTag: 'research_error',
            });
            clearForceFinalizeWatchdog();
            ctx.setState({ stage: 'idle' });
            return { status: 'completed', nextStep: null };
          } catch (guestErr) {
            // Abort (cancel/unmount) — let the stream drop quietly; the server
            // task keeps running and a refresh re-attaches via serverTaskId.
            if ((guestErr as any)?.name === 'AbortError') {
              return { status: 'completed', nextStep: null };
            }
            finalized = true;
            const isRate = guestErr instanceof GuestResearchRateLimitError;
            const msg = isRate
              ? (guestErr as GuestResearchRateLimitError).message
              : 'deep research could not be started — please try again.';
            stream.finalize({
              text: msg,
              html: generateSpecialMessageTemplate({
                header: { title: isRate ? 'too many requests' : 'research failed', icon: '⚠' },
                sections: [{ type: 'text', content: msg }],
                theme: 'amber',
              }),
              sourceTag: 'research_error',
            });
            clearForceFinalizeWatchdog();
            ctx.setState({ stage: 'idle' });
            return { status: 'completed', nextStep: null };
          }
        }
        // ====================================================================
        // END Point 6 guest server path. Below is the legacy in-browser path,
        // used by any non-guest legacy host (migrated surface uses prest0nRunTurn).
        // ====================================================================

        try {
          safeBroadcastBridgeEvent({
            event: 'workflow.progress',
            payload: {
              stage: 'stage2',
              message: 'Running deep research',
            },
          });

          // Stage 2: Build research brief with conversation history for context
          const documentAnalysisSection = combinedAttachmentContent
            ? `\n\n--- DOCUMENT ANALYSIS (from attached files) ---\n${combinedAttachmentContent}`
            : '';

          // Get conversation history for Stage 2 context
          // CRITICAL: Pass query to detect continuity signals ("it", "that", etc.)
          // Cap at 5 messages (not 10) to reduce token count — Stage 2 already has
          // Stage 1 findings + document analysis, so full conversation context is redundant.
          const stage2ConversationContext = formatConversationHistory(ctx.sharedContext?.messageHistory, 5, query);

          const stage2Query = [
            `Original Query: ${query}`,
            stage2ConversationContext,
            `User Clarifications: ${allClarifications.join('\n')}`,
            `Stage 1 Findings: ${stage1Findings}`,
            `Stage 1 Questions Asked: ${clarifyingQuestions.join('; ')}`,
            documentAnalysisSection,
          ].filter(Boolean).join('\n\n');

          // Stage 2 uses the direct Cloud Function URL, so the remaining regression
          // risk is the client/proxy 300s ceiling on a slow reasoning pass. Give the
          // brief step more headroom, then continue to Stage 3 with a synthesized
          // brief if Stage 2 still hits a transient timeout after retries.
          let stage2Result: Awaited<ReturnType<typeof runPerplexityResearch>> | null = null;
          let stage2FallbackReason: string | null = null;
          try {
            // T7/T8 fix (2026-05-11): mirror language for Stage 2.
            // Detection redone here because this lambda is a separate
            // tool-action handler (fires after user submits clarifications)
            // and the Stage-1 lambda's `drInputLanguage` is out of scope.
            const stage2InputLanguage = detectInputLanguage(query);
            const stage2SystemPromptLocalized =
              buildLanguageDirective(stage2InputLanguage) + STAGE2_SYSTEM_PROMPT;

            stage2Result = await runPerplexityResearch({
              query: stage2Query,
              systemPrompt: stage2SystemPromptLocalized,
              // D14 slice 3b: route name is the routing input (stage 2 rides
              // the qualification pre-stage tier); model/reasoningEffort feed
              // only the deploy-skew fallback.
              route: 'deep-research-qualification',
              model: PERPLEXITY_DEEP_RESEARCH_STAGE2_MODEL,
              reasoningEffort: PERPLEXITY_DEEP_RESEARCH_STAGES_1_2_REASONING_EFFORT,
              searchContextSize: PERPLEXITY_SEARCH_CONTEXT_SIZE,
              // Use direct Cloud Function URL to bypass Firebase Hosting's 60s rewrite timeout.
              // sonar-reasoning-pro with high reasoning effort routinely takes 60-120s.
              proxyUrl: PERPLEXITY_PROXY_URL_DIRECT,
              // Stage 2 builds a full research strategy from Stage 1 findings + document analysis.
              // 420s client timeout gives reasoning_effort=high adequate headroom beyond
              // the prior 300s ceiling that was occasionally exceeded.
              // On justiceisurright, we allow more retries to prevent 429 rate limit drops.
              // Bounded worst case: stage 2 is only the research BRIEF, not the
              // full report. Previously 420s x 4 retries = up to 28 min of silent
              // grinding on a flaky connection (matched Malaika's 29-min hang,
              // 2026-06-03). Cap at 240s x 2 retries = ~8 min worst case. The
              // brief does not need 7-minute reasoning headroom.
              timeout: 240000,
              maxRetries: 2,
              skipFallback: true,
              onRetry: (attempt, maxRetries, _error, retryAfterMs) => {
                // Show retry notification on every attempt so user sees progress
                stream.update({
                  text: `retry ${attempt}`,
                  html: getPendingWidgetHtml(`retry ${attempt}`),
                });
              },
              onRetryStart: (attempt, maxRetries) => {
                stream.update({
                  text: `retrying research brief... (attempt ${attempt + 1}/${maxRetries})`,
                  html: getPendingWidgetHtml(`retrying research brief (attempt ${attempt + 1}/${maxRetries})`),
                });
              },
            });
          } catch (stage2Err: any) {
            const cls = classifyPerplexityError(stage2Err);
            // Surface auth/config failures; they are permanent and must not be masked.
            if (cls.is401Error || cls.isApiKeyIssue) {
              throw stage2Err;
            }
            // Only absorb transient/timeout failures. Any other unexpected error
            // still propagates to the outer catch so it can be surfaced properly.
            if (!(cls.isTimeout || cls.isServerError || cls.isRateLimit || cls.isNetworkError)) {
              throw stage2Err;
            }
            stage2FallbackReason = cls.isTimeout
              ? 'research brief timed out'
              : cls.isRateLimit
                ? 'research brief rate-limited'
                : cls.isNetworkError
                  ? 'research brief connection interrupted'
                  : 'research brief unavailable';
            console.warn('[DeepResearch] Stage 2 failed, synthesizing fallback brief and continuing to Stage 3', {
              reason: stage2FallbackReason,
              status: stage2Err?.status,
              code: stage2Err?.code,
              message: stage2Err?.message,
            });
            stream.update({
              text: `${stage2FallbackReason} - continuing with preliminary findings`,
              html: getPendingWidgetHtml('continuing with preliminary findings'),
            });
          }

          // Store Stage 2 token usage for recording AFTER stream.finalize() (fixes timing issue)
          const stage2TokenUsage = stage2Result?.tokenUsage;
          if (stage2Result && stage2TokenUsage) {
            console.log('[DeepResearch] Stage 2 tokenUsage.model:', stage2TokenUsage.model);
            // ENRICHMENT: Estimate reasoning tokens from think blocks if API didn't provide them
            const stage2RawText = stage2Result.text || '';
            const stage2ThinkBlocks: string[] = stage2RawText.match(/<think>[\s\S]*?<\/think>/gi) || [];
            const stage2ThinkChars = stage2ThinkBlocks.reduce((sum: number, b: string) => sum + b.length, 0);
            if (stage2TokenUsage.reasoningTokens === undefined && stage2ThinkChars > 0) {
              stage2TokenUsage.reasoningTokens = Math.ceil(stage2ThinkChars / 4);
            }
            if (stage2TokenUsage.citationTokens === undefined && stage2Result.citations && stage2Result.citations.length > 0) {
              stage2TokenUsage.citationTokens = stage2Result.citations.length * 50;
            }
          }

          // MT-QA-BUG-008 (2026-05-08 G2 regression): capture Stage 2's
          // citations[] so that — even if Stage 3's streamed response drops
          // them (OpenRouter proxy variants sometimes do not forward the
          // citations block) — we still have authoritative URLs to back the
          // bracket citations the model emits inline. These are threaded
          // into Stage 3's prompt as AUTHORITATIVE_SOURCES and, after Stage
          // 3 completes, merged into perplexityCitations as a fallback.
          const stage2Citations: string[] = Array.isArray(stage2Result?.citations)
            ? (stage2Result!.citations as string[]).filter((u) => typeof u === 'string' && u.startsWith('http'))
            : [];

          let brief = '';
          let additionalContext = '';
          if (stage2Result) {
            try {
              const parsed = JSON.parse(stage2Result.text);
              brief = parsed.researchBrief || stage2Result.text;
              additionalContext = parsed.additionalContext || '';
            } catch {
              brief = stage2Result.text;
            }
          } else {
            // Fallback brief synthesized from Stage 1 findings + user clarifications.
            // Gives Stage 3 (sonar-deep-research) enough context to produce a complete
            // report even though the Stage 2 strategy step was unavailable.
            brief = [
              `FALLBACK RESEARCH BRIEF (Stage 2 ${stage2FallbackReason ?? 'unavailable'} - synthesized from prior context)`,
              '',
              `Original Query: ${query}`,
              `User Clarifications: ${allClarifications.join('\n') || '(none)'}`,
              `Stage 1 Preliminary Findings: ${stage1Findings || '(not available)'}`,
              `Stage 1 Clarifying Questions Asked: ${clarifyingQuestions.join('; ') || '(none)'}`,
            ].join('\n');
          }

          const stage2Brief = brief + (additionalContext ? `\n\nAdditional Context: ${additionalContext}` : '');

          // Stage 3: Execute deep research with conversation history for full context
          const documentsSection = combinedAttachmentContent
            ? `\n\n--- DOCUMENT ANALYSIS (from attached files) ---\n${combinedAttachmentContent}`
            : '';

          // Get conversation history for Stage 3 context - ensures research has full awareness of prior conversation
          // CRITICAL: Pass query to detect continuity signals ("it", "that", etc.)
          const stage3ConversationContext = formatConversationHistory(ctx.sharedContext?.messageHistory, 10, query);

          // MT-QA-BUG-008: inject Stage 2's grounded citations as
          // AUTHORITATIVE_SOURCES so Stage 3 can anchor bracket citations
          // ([25], [28], …) in real URLs the Research Brief was built from.
          const authoritativeSourcesSection = stage2Citations.length > 0
            ? `\n\nAUTHORITATIVE_SOURCES (from Stage 2 grounded research — prefer these URLs when citing):\n${stage2Citations
                .map((url, i) => `[${i + 1}] ${url}`)
                .join('\n')}`
            : '';

          const fullContext = `
RESEARCH BRIEF:
${stage2Brief}

ORIGINAL USER QUERY:
${query}
${stage3ConversationContext}
USER-PROVIDED CLARIFICATIONS:
${allClarifications.join('\n')}

STAGE 1 PRELIMINARY FINDINGS:
${stage1Findings}${documentsSection}${authoritativeSourcesSection}

Unless otherwise specified, for formatting and style, ignore all inputs (query, attachments, documents, etc.) and use professional, organized formatting throughout the report while still reading all information, attachment, and documents given for info. Every bracket citation you emit ([1], [2], …) MUST be backed by a real URL in the final "References" section; if AUTHORITATIVE_SOURCES are provided above, use those URLs rather than inventing new ones, and the References section MUST list every URL used.
`.trim();

          let finalText = '';
          let researchTokenUsage: TokenUsageMetadata | undefined;
          let perplexityCitations: string[] | undefined;

          // Hoisted so the force-finalize watchdog (installed after the stream
          // resolves) and detached background upload continuation can reach
          // the latest best-known text/title without relying on block-scoped
          // variables inside the try.
          let cleanedContent = '';
          let reportTitle = '';
          // Hoisted token-usage accumulator so the force-finalize watchdog (which
          // finalizes defensively without the normal allTokenUsages built far
          // below) still attaches cost/token data. Without this, a watchdog
          // finalize produced a complete report with the cost popout showing
          // "no extensive compute was necessary" on a billable deep-research run
          // (Malaika, 2026-06-03). Seed with Stage 1 lineage from state.
          let watchdogTokenUsages: TokenUsageMetadata[] = [...(ctx.state.tokenUsages || [])];

          // MT-QA-BUG-001: resolve the active tenant's firm branding once,
          // up-front. It's threaded into the system prompt for the research
          // call (so the model doesn't self-identify as Fernando's firm for
          // T1/T2 tenants) AND into the PDF/DOCX generator below.
          const tenantBranding = await fetchCurrentTenantBranding();

          // Stage 3: Streaming deep research.
          // Uses runPerplexityResearchStream() to progressively display the report.
          // Streaming chunks provide natural keepalive (no heartbeat needed).
          // Uses direct Cloud Function URL to bypass Firebase Hosting's 60s rewrite timeout.
          stream.update({
            text: 'Conducting in-depth legal research. This typically takes 3-5 minutes. The report will stream in as it generates.',
            html: getPendingWidgetHtml('deep researching'),
          });

          // Throttle UI updates to avoid excessive re-renders.
          let lastStreamUpdate = 0;
          const STREAM_THROTTLE_MS = 500;

          // Use the tenant branding resolved above in the system prompt.
          // T7/T8 fix (2026-05-11): mirror language for Stage 3 (the final
          // user-facing report). Detected from the original user query, NOT
          // from `fullContext` which folds in the English-language Stage-2
          // brief that would self-anchor English regardless of input language.
          const stage3InputLanguage = detectInputLanguage(query);
          const stage3SystemPromptLocalized =
            buildLanguageDirective(stage3InputLanguage) +
            buildDeepResearchSystemPrompt(
              'See the user message below for the complete research query, context, and all supporting details.',
              { firmName: tenantBranding.firmName },
            );

          const result = await runPerplexityResearchStream({
            query: fullContext + '\n\nBased on ALL the above context, conduct thorough legal research and produce a complete report following all instructions in your system prompt. Address the user\'s specific situation and tailor all analysis, strategy, and recommendations to their particular facts and circumstances. The report must be thorough and detailed, covering all fifteen sections (I-XV) outlined in your instructions including appendices and complete citations. Do not summarize or abbreviate — produce the full-length report. Ensure to be thorough in your research using reasoning = high.',
            systemPrompt: stage3SystemPromptLocalized,
            // D14 slice 3b: route name is the routing input — the server stamps
            // the tier-14 ladder + effort ('high' now lives in the SSOT, not
            // here). model/reasoningEffort feed only the deploy-skew fallback;
            // TESTING_MODE's cheaper-model half therefore now applies only on
            // that fallback path — model choice is the server's.
            route: 'deep-research',
            model: PERPLEXITY_DEEP_RESEARCH_MODEL,
            maxTokens: PERPLEXITY_DEEP_RESEARCH_MAX_TOKENS,
            searchContextSize: PERPLEXITY_SEARCH_CONTEXT_SIZE,
            reasoningEffort: PERPLEXITY_REASONING_EFFORT,
            // NOTE: temperature intentionally NOT passed for sonar-deep-research.
            // The model does not support it — sending it triggers silent reasoning downgrade.
            proxyUrl: PERPLEXITY_PROXY_URL_DIRECT,
            // 2026-07-07: the COMPOSER deep-research report is billed to the
            // signed-in user's OWN wallet via the client-side recordTransaction
            // below (stage3Usage). It must NOT also set meterDeepResearch —
            // that routes perplexityProxy → recordWidgetExpense → the fixed
            // HIDALGO widget wallet, which (a) double-charged (server Hidalgo +
            // client user for one call) and (b) billed the wrong wallet.
            // Follow-up (Phase 3): route this through recordAccountExpense with
            // the user's account so the OpenRouter-authoritative cost (incl.
            // Perplexity search fees) debits the correct wallet with idempotency,
            // then flip the client record to meteredServerSide.
            meterSummary: 'deep research',
            onChunk: (_chunk: string, accumulated: string) => {
              // stripThinkingBlocks handles both complete and incomplete think tags:
              //   - Complete <think>...</think> → removed
              //   - Unclosed <think>... (mid-stream) → everything from <think> to end hidden
              const visibleText = stripThinkingBlocks(accumulated);

              // Skip update if no visible content yet (model may emit think blocks first)
              if (!visibleText.trim()) return;

              // Throttle DOM updates
              const now = Date.now();
              if (now - lastStreamUpdate < STREAM_THROTTLE_MS) return;
              lastStreamUpdate = now;

              stream.update({
                text: visibleText,
                html: formatResearchToHtml(visibleText),
              });
            },
            onRetry: (attempt, maxRetries, _error, retryAfterMs) => {
              // Silent retry: only show notification on final attempt to keep UX confident
              if (attempt >= maxRetries - 1) {
                const notification = buildRetryNotification(
                  PERPLEXITY_DEEP_RESEARCH_MODEL, 'deep research', attempt, maxRetries, retryAfterMs,
                );
                stream.update(notification);
              }
            },
            onRetryStart: (attempt, maxRetries) => {
              // Silent retry: only show notification on final attempt to keep UX confident
              if (attempt >= maxRetries - 1) {
                stream.update({
                  text: `retrying deep research... (attempt ${attempt + 1}/${maxRetries})`,
                  html: getPendingWidgetHtml(`retrying deep research (attempt ${attempt + 1}/${maxRetries})`),
                });
              }
            },
          });

          finalText = result.text || '';
          researchTokenUsage = result.tokenUsage;
          perplexityCitations = result.citations;

          safeBroadcastBridgeEvent({
            event: 'workflow.progress',
            payload: {
              stage: 'stage3',
              message: 'Synthesizing deep research output',
            },
          });

          // SAFETY NET: force-finalize watchdog.
          // The Stage 3 stream resolved successfully. If anything between here
          // and stream.finalize() hangs (document generation, storage upload,
          // etc.) we must not leave the user on "deep researching" forever.
          // After 120s from stream resolution, finalize defensively using the
          // best text we have (cleanedContent once computed, else raw finalText).
          forceFinalizeTimeoutId = setTimeout(() => {
            if (finalized) return;
            console.warn('[DeepResearch] Force-finalize watchdog fired after 120s');
            const fallbackText =
              (cleanedContent && cleanedContent.trim().length > 0 && cleanedContent)
              || (finalText && finalText.trim().length > 0 && finalText)
              || 'Research completed, but finalization was delayed. The report may be incomplete.';
            let fallbackHtml: string;
            try {
              fallbackHtml = formatResearchToHtml(fallbackText);
            } catch {
              fallbackHtml = `<div>${fallbackText}</div>`;
            }
            try {
              finalized = true;
              stream.finalize({
                text: fallbackText,
                html: fallbackHtml,
                sourceTag: 'research_result',
                // Attach best-known token usage so the cost popout never shows
                // "no compute" on a billable deep-research run finalized by the
                // watchdog (Malaika, 2026-06-03).
                tokenUsages: watchdogTokenUsages.length > 0 ? watchdogTokenUsages : undefined,
              });
            } catch (watchdogErr) {
              console.error(
                '[DeepResearch] Watchdog finalize failed:',
                watchdogErr
              );
            }
          }, 120_000);

          if (researchTokenUsage) {
            console.log('[DeepResearch] Stage 3 tokenUsage.model:', researchTokenUsage.model);
          }

          // DIAGNOSTIC: Log raw vs stripped content breakdown to understand think block ratio
          const rawText = finalText;
          const thinkBlocks: string[] = rawText.match(/<think>[\s\S]*?<\/think>/gi) || [];
          const thinkContentLength = thinkBlocks.reduce((sum: number, b: string) => sum + b.length, 0);
          console.log('[DeepResearch] Content breakdown:', {
            rawLength: rawText.length,
            thinkBlockCount: thinkBlocks.length,
            thinkContentChars: thinkContentLength,
            visibleContentChars: rawText.length - thinkContentLength,
            outputTokens: researchTokenUsage?.outputTokens,
            reasoningTokens: researchTokenUsage?.reasoningTokens,
            finishReason: researchTokenUsage?.finishReason,
            maxTokensSent: PERPLEXITY_DEEP_RESEARCH_MAX_TOKENS,
            completionVsMax: `${researchTokenUsage?.outputTokens || 0} / ${PERPLEXITY_DEEP_RESEARCH_MAX_TOKENS}`,
          });

          // ENRICHMENT: Populate reasoning/citation/search fields from content analysis
          // when the API doesn't return them natively (common with OpenRouter proxy).
          // This ensures the info popup displays meaningful Perplexity-specific metrics.
          if (researchTokenUsage) {
            // Estimate reasoning tokens from <think> blocks if not provided by API
            if (researchTokenUsage.reasoningTokens === undefined && thinkContentLength > 0) {
              // ~4 chars per token is a reasonable English text estimate
              researchTokenUsage.reasoningTokens = Math.ceil(thinkContentLength / 4);
            }
            // Use citation URL count as citation token proxy if not provided
            if (researchTokenUsage.citationTokens === undefined && perplexityCitations && perplexityCitations.length > 0) {
              // Each citation URL averages ~50 tokens of grounding context
              researchTokenUsage.citationTokens = perplexityCitations.length * 50;
            }
            // searchQueries: Perplexity deep research typically runs 20-50+ searches
            // but this count isn't exposed in the stream — mark as present but unknown
            if (researchTokenUsage.searchQueries === undefined) {
              // Use total_tokens heuristic: deep research with >10K output usually ran 20+ searches
              const outputTokens = researchTokenUsage.outputTokens || 0;
              if (outputTokens > 10000) {
                researchTokenUsage.searchQueries = Math.max(20, Math.ceil(outputTokens / 1000));
              } else if (outputTokens > 5000) {
                researchTokenUsage.searchQueries = Math.max(10, Math.ceil(outputTokens / 1500));
              }
            }
          }

          // Keep the hoisted watchdog accumulator current so a defensive
          // watchdog finalize still carries real cost/token data (see decl).
          // Stage 1 was already seeded; append Stage 2 (if it produced usage)
          // and Stage 3 here, the moment Stage 3 usage is enriched.
          {
            const wd: TokenUsageMetadata[] = [...(ctx.state.tokenUsages || [])];
            const s2 = stage2Result?.tokenUsage;
            if (s2) wd.push({ ...s2, summary: 'built research strategy' });
            if (researchTokenUsage) wd.push({ ...researchTokenUsage, summary: 'comprehensive case study' });
            if (wd.length > 0) watchdogTokenUsages = wd;
          }

          // Process final result: strip thinking blocks, then ensure references section exists.
          // MT-QA-BUG-008: merge Stage 2 citations with Stage 3 citations so
          // reports still get a real References section when the Stage 3
          // stream drops the citations block (OpenRouter proxy variants).
          const rawContent = stripThinkingBlocks(rawText);
          const mergedCitations: string[] = (() => {
            const seen = new Set<string>();
            const out: string[] = [];
            for (const url of (perplexityCitations || [])) {
              if (typeof url === 'string' && url.startsWith('http') && !seen.has(url)) {
                seen.add(url);
                out.push(url);
              }
            }
            for (const url of stage2Citations) {
              if (!seen.has(url)) {
                seen.add(url);
                out.push(url);
              }
            }
            return out;
          })();
          if ((perplexityCitations?.length || 0) === 0 && stage2Citations.length > 0) {
            console.log(
              `[DeepResearch] Stage 3 returned no citations; falling back to ${stage2Citations.length} Stage 2 citation URLs for References section (MT-QA-BUG-008)`
            );
          }
          cleanedContent = ensureReferencesSection(
            rawContent,
            mergedCitations
          );

          // PRE-215 (2026-05-14): Recover References when the deep-research
          // pipeline truncates mid-stream and BOTH Stage 3 and Stage 2 produce
          // no citations -- which leaves the user with a body containing inline
          // `[N]` bracket cites but a "No external sources were cited" stub.
          // Three independent symptoms must all be true to trigger recovery:
          //   1. mergedCitations is empty (Stage 2 and Stage 3 both dropped them)
          //   2. the rendered body still references at least one bracket cite
          //      (otherwise there's nothing to back -- the empty stub is fine)
          //   3. EITHER finishReason='length' (clear truncation signal) OR the
          //      content has no References section header at all
          //
          // Recovery: one short non-streaming sonar-pro call (fast, ~5-15s,
          // cheap relative to the already-run deep research) asking for the
          // authoritative sources on the query. Pull its citations[], merge
          // with any inline URLs, rewrite the References section.
          //
          // Refs: Linear PRE-215, parent QA thread C0AM8U804KZ/1778481193.723179.
          // Fingerprint string `pre-215-citations-recovery-v20260514` is grep-able
          // in Cloud Run logs to confirm the path fired.
          const referencesSectionIdx = findReferencesSectionIndex(cleanedContent);
          const hasBracketCites = /\[\d+\]/.test(cleanedContent);
          const looksTruncated = researchTokenUsage?.finishReason === 'length';
          const referencesAreMissing = referencesSectionIdx === -1
            || /no external sources were cited/i.test(
                cleanedContent.slice(referencesSectionIdx).slice(0, 200)
              );
          if (
            mergedCitations.length === 0
            && hasBracketCites
            && referencesAreMissing
            && (looksTruncated || referencesSectionIdx === -1)
          ) {
            console.log(
              '[DeepResearch] pre-215-citations-recovery-v20260514: '
              + 'Stage 3 + Stage 2 produced no citations but body has bracket '
              + 'cites; firing sonar-pro citations-recovery call. '
              + 'finishReason=' + (researchTokenUsage?.finishReason || 'unknown')
              + ' outputTokens=' + (researchTokenUsage?.outputTokens || 0)
              + ' refIdx=' + referencesSectionIdx
            );
            try {
              const recoveryQuery = (
                'List the 10-15 most authoritative sources you would cite for '
                + 'the following legal-research question. Return concise titles '
                + 'and URLs only. Question:\n\n' + query
              );
              const recoveryResult = await runPerplexityResearch({
                query: recoveryQuery,
                // 2026-08-15: registry-resolved tier-14 Fallback 1 (was a
                // hard-coded 'sonar-pro' literal — CLAUDE.md violation).
                // D14 slice 3b: deliberately NOT flipped to a route — no task
                // route resolves sonar-pro at rung 0 (every deep-research
                // route's rung 0 is sonar-deep-research, which cannot answer a
                // 30s citation-listing call). Owner call pending, same class
                // as the video-script binding: either a dedicated SSOT route
                // for citation recovery, or accept the rung-0 model change.
                model: PERPLEXITY_DEEP_RESEARCH_FALLBACK_MODEL,
                maxTokens: 4000,
                proxyUrl: PERPLEXITY_PROXY_URL_DIRECT,
                // 30s wall-clock cap on the recovery call so a slow Perplexity
                // response never extends the user's wait past the truncated body
                // they already have in the chat.
                timeout: 30000,
                maxRetries: 1,
                signal: ctx.abortSignal,
              });
              const recoveredCitations = Array.isArray(recoveryResult?.citations)
                ? (recoveryResult.citations as string[]).filter(
                    (u) => typeof u === 'string' && u.startsWith('http')
                  )
                : [];
              if (recoveredCitations.length > 0) {
                console.log(
                  '[DeepResearch] pre-215-citations-recovery-v20260514: '
                  + 'recovered ' + recoveredCitations.length + ' citations'
                );
                // Re-run ensureReferencesSection with the recovered citations
                // merged in. Because the cleanedContent already has the stub
                // "No external sources were cited" section appended by the
                // earlier call, the existing-References-rewrite branch in
                // ensureReferencesSection (MT-QA-BUG-008 path) will replace
                // the stub with the real list.
                cleanedContent = ensureReferencesSection(
                  cleanedContent,
                  recoveredCitations
                );
              } else {
                console.warn(
                  '[DeepResearch] pre-215-citations-recovery-v20260514: '
                  + 'recovery call returned 0 citations; leaving stub in place'
                );
              }
            } catch (err) {
              const errMsg = err instanceof Error ? err.message : String(err);
              console.warn(
                '[DeepResearch] pre-215-citations-recovery-v20260514: '
                + 'recovery call failed; leaving stub in place. error=' + errMsg
              );
            }
          }

          // Detect token-limit truncation and append a user-friendly notice
          if (researchTokenUsage?.finishReason === 'length') {
            console.warn('[DeepResearch] Report truncated: finish_reason=length. outputTokens:', researchTokenUsage.outputTokens, '/', PERPLEXITY_DEEP_RESEARCH_MAX_TOKENS);

            // dr-truncated-refs-repair-v20260815: on finish_reason=length the
            // cut lands in the LAST section — References. A partially-emitted
            // list passes ensureReferencesSection's non-empty gate (>=80 chars
            // or any URL) and previously shipped exactly as truncated, silently
            // discarding the authoritative citations[] array. Additively append
            // every citation URL missing from the (possibly cut) section,
            // continuing the numbering — intact entries are never touched.
            const refsRepair = appendMissingCitationsToReferences(cleanedContent, mergedCitations);
            if (refsRepair.appended > 0) {
              console.log(
                `[DeepResearch] dr-truncated-refs-repair-v20260815: appended ${refsRepair.appended} citation(s) missing from the truncated References section`
              );
              cleanedContent = refsRepair.content;
            }

            cleanedContent += '\n\n---\n\n> **Note:** This research report is comprehensive but reached the maximum output length. Some content may have been cut off. Consider narrowing your query for a more focused report.';
          }
          reportTitle = generateConciseTitle(cleanedContent, query);

          // CRITICAL: Force final update to ensure no content is lost due to throttling
          // The 500ms throttle may have skipped the very last chunk
          if (cleanedContent.trim().length > 0) {
            const finalHtmlUpdate = formatResearchToHtml(cleanedContent);
            stream.update({
              text: cleanedContent,
              html: finalHtmlUpdate,
            });
          }

          // Build PDF and DOCX with Firebase Storage upload for cross-device persistence
          // Get current user UID for Storage path
          const auth = getAuth();
          const currentUid = auth.currentUser?.uid;
          // MT-QA-BUG-001: reuse tenant branding resolved earlier in this handler.

          // Wrap document generation in try-catch to handle failures gracefully
          // If document generation fails, we still want to finalize with the research content
          let pdf: File | undefined;
          let docx: File | undefined;
          let finalPdfSerialized: import('../../../components/GlobalChatSystem/types').SerializedAttachment | undefined;
          let finalDocxSerialized: import('../../../components/GlobalChatSystem/types').SerializedAttachment | undefined;
          let finalDeliverableUrl: string | undefined;
          let documentGenerationFailed = false;
          let docGenTimedOut = false;

          // Post-stream document generation runs under a strict total-time
          // budget. On a 2MB+ report, buildResearchPdf + buildResearchDocx +
          // Firebase Storage upload can legitimately exceed a minute, and
          // sometimes hangs entirely (wedged uploadBytes, slow network). We
          // must not let that delay stream.finalize() — users see "deep
          // researching" indefinitely otherwise. On timeout we finalize
          // without attachments and continue the upload in the background.
          const DOC_GEN_BUDGET_MS = 90_000;
          console.log('[DeepResearch] Post-stream document generation budget: 90s');

          // Start the generation exactly once. Keep the promise so the
          // detached background follow-up can await its completion after the
          // budget expires without re-running PDF/DOCX building.
          const docGenStartedAt = Date.now();
          const docGenPromise = generateDualFormatDocumentsWithStorage(
            {
              type: 'research',
              title: reportTitle,
              prompt: reportTitle,
              content: cleanedContent,
              // MT-QA-BUG-001: thread tenant firm name into report PDF/DOCX
              // cover page, footer, copyright, and metadata.
              translations: { firmName: tenantBranding.firmName },
            },
            // Only upload to Storage if user is authenticated
            currentUid ? { uid: currentUid, messageId: stream.id } : undefined
          );

          let docGenResult:
            | Awaited<ReturnType<typeof generateDualFormatDocumentsWithStorage>>
            | undefined;
          let budgetTimerHandle: ReturnType<typeof setTimeout> | undefined;
          try {
            docGenResult = await Promise.race([
              docGenPromise,
              new Promise<never>((_, reject) => {
                budgetTimerHandle = setTimeout(() => {
                  docGenTimedOut = true;
                  reject(new Error('doc-gen-budget-exceeded'));
                }, DOC_GEN_BUDGET_MS);
              }),
            ]);
            console.log(
              `[DeepResearch] Document generation succeeded in ${Date.now() - docGenStartedAt}ms`
            );
          } catch (docGenError) {
            if (docGenTimedOut) {
              console.warn(
                '[DeepResearch] Document generation exceeded budget; finalizing without attachments and retrying upload in background'
              );
            } else {
              documentGenerationFailed = true;
              console.error(
                `[DeepResearch] Document generation failed after ${Date.now() - docGenStartedAt}ms:`,
                docGenError
              );
              console.warn(
                '[DeepResearch] Continuing without PDF/DOCX attachments - research content will still be displayed'
              );
            }
          } finally {
            if (budgetTimerHandle) clearTimeout(budgetTimerHandle);
          }

          if (docGenResult && !docGenTimedOut && !documentGenerationFailed) {
            const {
              pdf: generatedPdf,
              docx: generatedDocx,
              pdfSerialized,
              docxSerialized,
              pdfSerializedWithStorage,
              docxSerializedWithStorage,
              uploadStatus,
            } = docGenResult;

            pdf = generatedPdf;
            docx = generatedDocx;

            // Log upload status for debugging cross-device persistence issues
            if (uploadStatus) {
              if (uploadStatus.error) {
                console.warn('[DeepResearch] Storage upload issue:', uploadStatus.error, {
                  pdfUploaded: uploadStatus.pdfUploaded,
                  docxUploaded: uploadStatus.docxUploaded,
                });
              } else if (uploadStatus.pdfUploaded && uploadStatus.docxUploaded) {
                console.log('[DeepResearch] Storage upload successful - attachments will persist cross-device');
              }
            }

            // Use Storage-backed serialized attachments if available, fallback to localStorage-only
            finalPdfSerialized = pdfSerializedWithStorage || pdfSerialized;
            finalDocxSerialized = docxSerializedWithStorage || docxSerialized;
            finalDeliverableUrl =
              finalPdfSerialized?.storageUrl
              || finalDocxSerialized?.storageUrl
              || finalPdfSerialized?.dataUrl
              || finalDocxSerialized?.dataUrl
              || undefined;

            // Validate that final attachments have downloadable content
            const pdfHasContent = !!(finalPdfSerialized?.storageUrl || finalPdfSerialized?.dataUrl);
            const docxHasContent = !!(finalDocxSerialized?.storageUrl || finalDocxSerialized?.dataUrl);
            if (!pdfHasContent) {
              console.error('[DeepResearch] PDF attachment has no downloadable content (no storageUrl or dataUrl)');
            }
            if (!docxHasContent) {
              console.error('[DeepResearch] DOCX attachment has no downloadable content (no storageUrl or dataUrl)');
            }

            console.log('[DeepResearch] Document generation successful:', {
              pdfName: pdf?.name,
              docxName: docx?.name,
              pdfHasContent,
              docxHasContent,
            });
          }

          // Collect token usages for the final report
          // Include Stage 1 usage from state so info popup shows full agentic workflow lineage
          const parentTokenUsages = ctx.state.tokenUsages || [];
          const allTokenUsages: TokenUsageMetadata[] = [...parentTokenUsages];
          if (stage2TokenUsage) {
            allTokenUsages.push({ ...stage2TokenUsage, summary: 'built research strategy' });
          }
          const stage3Usage = researchTokenUsage;
          if (stage3Usage) {
            allTokenUsages.push({ ...stage3Usage, summary: 'comprehensive case study' });
          }

          // Store Stage 3 report in workflow output store for generalized button context passing
          const stage3OutputId = storeWorkflowOutput({
            id: `dr-stage3-${Date.now()}`,
            content: cleanedContent,
            title: reportTitle,
            timestamp: new Date(),
            source: 'research',
            metadata: {
              originalQuery: query,
              clarifications: allClarifications,
            },
            tokenUsages: allTokenUsages.length > 0 ? allTokenUsages : undefined,
          });

          // Build data attributes for generalized context passing (same pattern as Stage 1)
          const buttonDataAttrs = { 'workflow-output-id': stage3OutputId };

          // Generate action buttons HTML with data attributes for context retrieval
          // Colors match each workflow's theme accent
          // 2026-08-22 owner directive: the write-video-script button no longer
          // attaches to special elements; 'translate' stays, on the platform token.
          const actionButtonsHtml = generateResearchActionButtonsHtml([
            { label: 'translate', id: TRANSLATE_BUTTON_IDS.translate, background: PREST0_PRIMARY_CTA_BG, dataAttributes: buttonDataAttrs },
          ], { layout: 'vertical' });

          // Finalize streaming message with action buttons passed separately
          // Conditionally include attachments only if document generation succeeded
          const researchHtml = formatResearchToHtml(cleanedContent);

          // Build attachments arrays only if document generation succeeded
          // within the budget. If it timed out, we finalize without them and
          // let the detached continuation below post a follow-up when ready.
          const finalAttachments: File[] | undefined =
            pdf && docx && !documentGenerationFailed && !docGenTimedOut ? [pdf, docx] : undefined;
          const finalAttachmentsSerialized =
            finalPdfSerialized && finalDocxSerialized && !documentGenerationFailed && !docGenTimedOut
              ? [finalPdfSerialized, finalDocxSerialized]
              : undefined;

          // Mark finalized BEFORE calling stream.finalize so the 120s safety
          // watchdog can observe it and no-op if it fires during/after this.
          finalized = true;
          stream.finalize({
            text: cleanedContent,
            html: researchHtml,
            sourceTag: 'research_result',
            // Only include attachments if document generation succeeded
            attachments: finalAttachments,
            // Use Storage-backed attachments for cross-device download support
            attachmentsSerialized: finalAttachmentsSerialized,
            // Only pass tokenUsages array - DO NOT pass tokenUsage singular to avoid duplicates
            // (Stage 3 is already in allTokenUsages; passing both causes double-counting)
            tokenUsages: allTokenUsages.length > 0 ? allTokenUsages : undefined,
            workflowActionButtonsHtml: actionButtonsHtml,
            // Embed workflow output for downstream workflow chaining
            workflowOutput: {
              type: 'research_report',
              content: cleanedContent,
              title: reportTitle,
              timestamp: new Date(),
              sourceWorkflow: 'deep-research',
            },
          });
          clearForceFinalizeWatchdog();

          // Log if attachments were skipped due to generation failure
          if (documentGenerationFailed) {
            console.warn('[DeepResearch] Research finalized WITHOUT attachments due to document generation failure');
          } else if (docGenTimedOut) {
            console.warn('[DeepResearch] Research finalized WITHOUT attachments due to doc-gen budget timeout; background upload continues');
          } else if (!finalAttachments) {
            console.warn('[DeepResearch] Research finalized WITHOUT attachments (no generation result)');
          } else {
            console.log('[DeepResearch] Research finalized with attachments:', finalAttachments.length, 'files');
          }

          // Detached background continuation: if generation exceeded the 90s
          // budget, keep awaiting it (capped at another 60s) and, on success,
          // post a follow-up "PDF & DOCX ready" notice with download links
          // matching the attachmentsSerialized shape. Any failure here is
          // silently dropped — the user already has their research report.
          if (docGenTimedOut) {
            (async () => {
              let bgTimerHandle: ReturnType<typeof setTimeout> | undefined;
              try {
                const bgResult = await Promise.race([
                  docGenPromise,
                  new Promise<never>((_, reject) => {
                    bgTimerHandle = setTimeout(
                      () => reject(new Error('background-doc-gen-timeout')),
                      60_000
                    );
                  }),
                ]);
                const bgPdf = bgResult?.pdfSerializedWithStorage || bgResult?.pdfSerialized;
                const bgDocx = bgResult?.docxSerializedWithStorage || bgResult?.docxSerialized;
                const bgPdfUrl = bgPdf?.storageUrl || bgPdf?.dataUrl;
                const bgDocxUrl = bgDocx?.storageUrl || bgDocx?.dataUrl;
                if (!bgPdfUrl && !bgDocxUrl) {
                  console.warn('[DeepResearch] Background document generation finished with no downloadable URLs; skipping follow-up');
                  return;
                }
                const escape = (s: string) =>
                  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
                const linkParts: string[] = [];
                if (bgPdfUrl && bgPdf) {
                  linkParts.push(
                    `<a href="${escape(bgPdfUrl)}" target="_blank" rel="noopener" style="color:#1e40af;text-decoration:underline;font-weight:500;">${escape(bgPdf.name || 'report.pdf')}</a>`
                  );
                }
                if (bgDocxUrl && bgDocx) {
                  linkParts.push(
                    `<a href="${escape(bgDocxUrl)}" target="_blank" rel="noopener" style="color:#1e40af;text-decoration:underline;font-weight:500;">${escape(bgDocx.name || 'report.docx')}</a>`
                  );
                }
                const linksHtml = `<div style="font-size:0.9rem;color:#374151;">${linkParts.join(' &nbsp;·&nbsp; ')}</div>`;
                ctx.messaging.showSpecialMessage({
                  header: { title: 'PDF & DOCX ready', icon: '📎' },
                  sections: [{ type: 'custom', content: linksHtml }],
                  theme: 'blue',
                });
                console.log('[DeepResearch] Background document generation finished; follow-up notice posted');
              } catch (bgErr) {
                // Already finalized; do not spam UI with errors.
                console.warn(
                  '[DeepResearch] Background document generation did not complete:',
                  (bgErr as any)?.message
                );
              } finally {
                if (bgTimerHandle) clearTimeout(bgTimerHandle);
              }
            })();
          }

          // Record transactions AFTER message display (fixes timing issue)
          if (stage2TokenUsage) {
            ctx.recordTransaction({
              tokenUsage: stage2TokenUsage,
              source: 'agenticWorkflows',
              modelOverride: buildModelDisplay(stage2TokenUsage, '', PERPLEXITY_DEEP_RESEARCH_STAGE2_DISPLAY),
              summary: 'strategising research',
            });
          }
          if (stage3Usage) {
            ctx.recordTransaction({
              tokenUsage: stage3Usage,
              source: 'agenticWorkflows',
              modelOverride: buildModelDisplay(stage3Usage, '', PERPLEXITY_DEEP_RESEARCH_DISPLAY),
              summary: 'legal deep research',
            });
          }

          // Store the completed report
          const reportData = {
            content: cleanedContent,
            title: reportTitle,
            timestamp: new Date(),
          };
          ctx.sharedContext.setLastResearchReport(reportData);
          ctx.sharedContext.lastResearchReportRef.current = reportData;

          // Store as translatable content (include tokenUsages for downstream workflow lineage)
          const translatableData = {
            content: cleanedContent,
            title: reportTitle,
            timestamp: new Date(),
            source: 'research' as const,
            tokenUsages: allTokenUsages.length > 0 ? allTokenUsages : undefined,
          };
          ctx.sharedContext.setLastTranslatableContent(translatableData);
          ctx.sharedContext.lastTranslatableContentRef.current = translatableData;

          // Update state
          ctx.setState({
            stage: 'idle',
            reportContent: cleanedContent,
            reportTitle,
            tokenUsages: allTokenUsages,
          });

          safeBroadcastBridgeEvent({
            event: 'workflow.completed',
            payload: {
              summary: buildWorkflowSummary(cleanedContent, reportTitle),
              reportTitle,
              ...(finalDeliverableUrl ? { deliverableUrl: finalDeliverableUrl } : {}),
            },
          });

          return {
            status: 'completed',
            output: {
              content: cleanedContent,
              title: reportTitle,
            },
          };
        } catch (error) {
          console.error('[DeepResearch] Research step error:', error, {
            message: (error as any)?.message,
            status: (error as any)?.status,
            code: (error as any)?.code,
            stack: (error as any)?.stack?.split('\n').slice(0, 5),
          });

          // Classify error using numeric status, error code, and message text
          const { isTimeout, isRateLimit, isServerError, isNetworkError, is401Error, isApiKeyIssue } = classifyPerplexityError(error);

          let userFriendlyMessage = 'An error occurred during research. Please try again.';
          if (is401Error || isApiKeyIssue) {
            userFriendlyMessage = 'Research service is currently unavailable due to configuration issues. Please contact support.';
          } else if (isTimeout) {
            userFriendlyMessage = 'The research request timed out. Please try a simpler query or try again later.';
          } else if (isRateLimit) {
            userFriendlyMessage = 'The research provider is currently facing high demand. Please try again in a moment.';
          } else if (isServerError) {
            userFriendlyMessage = 'The research service is temporarily unavailable. Please try again in a moment.';
          } else if (isNetworkError) {
            userFriendlyMessage = 'The connection was interrupted. Please try again in a moment.';
          }
          // Owner directive 2026-08-13: platform errors carry the on-call
          // notice — but ONLY the classes the proxy actually reported
          // server-side (deferred from #1233). A client-side timeout or
          // network abort never reached a server that could file anything.
          if (researchFailureIsServerReported({ isServerError, isRateLimit, is401Error })) {
            userFriendlyMessage = withOncallNotice(userFriendlyMessage);
          }

          // For retryable errors (server errors, rate limits, client timeouts),
          // offer retry via awaiting-input instead of permanently failing the
          // workflow. This lets the user click "try again" without having to
          // restart the entire deep research workflow from scratch.
          // IMPORTANT: Exclude auth/config errors — these are permanent and retrying loops forever.
          if ((isServerError || isRateLimit || isTimeout || isNetworkError) && !(is401Error || isApiKeyIssue)) {
            const retryButtonHtml = `<div style="margin-top:12px;">
              <button data-button-id="${BUTTON_IDS.skip}" style="padding:8px 16px;background:#1e40af;color:white;border:none;border-radius:6px;cursor:pointer;font-size:0.85rem;">try again</button>
            </div>`;

            finalized = true;
            stream.finalize({
              text: userFriendlyMessage,
              html: `<p>${userFriendlyMessage}</p>`,
              payload: {
                kind: 'deep_research.error',
                toolId: 'deep-research',
                severity: 'error',
                data: { errorMessage: userFriendlyMessage, phase: 'stage3', retryable: true },
                summary: 'deep research: stage3 failed (retryable)',
              },
              workflowActionButtonsHtml: retryButtonHtml,
              sourceTag: 'research_error_retryable',
            });
            clearForceFinalizeWatchdog();

            ctx.setState({ stage: 'stage2_retry' });

            return {
              status: 'awaiting-input',
              carryForward: {
                query,
                documentAnalysis,
                stage1Findings,
                clarifyingQuestions,
                userClarificationText: allClarifications.join('\n'),
              },
            };
          }

          // Non-retryable errors: fail permanently
          finalized = true;
          stream.finalize({
            text: userFriendlyMessage,
            html: `<p>${userFriendlyMessage}</p>`,
            payload: {
              kind: 'deep_research.error',
              toolId: 'deep-research',
              severity: 'error',
              data: { errorMessage: userFriendlyMessage, phase: 'stage3', retryable: false },
              summary: 'deep research: stage3 failed (non-retryable)',
            },
            sourceTag: 'research_error',
          });
          clearForceFinalizeWatchdog();

          ctx.setState({ stage: 'idle' });

          safeBroadcastBridgeEvent({
            event: 'workflow.failed',
            payload: {
              stage: 'stage2',
              message: userFriendlyMessage,
            },
          });

          return {
            status: 'failed',
            error: new Error(`Unable to complete research: ${formatPerplexityError(error)}`),
          };
        }
      },

      cleanup: async (ctx: StepContext<DeepResearchState>) => {
        ctx.setState({ stage: 'idle' });
      },
    },
  ],

  isAuthorized: (userEmail?: string) => Boolean(userEmail) && isAgenticWorkflowsAuthorized(userEmail),

  theme: DEEP_RESEARCH_THEME,

  buttonIds: BUTTON_IDS,

  canTriggerFromButton: false,
};

// ============================================================================
// Export
// ============================================================================

export default deepResearchTool;
